/* BMN Enriched PDP, position-first decoration configurator + live pricing engine.
 *
 * One reusable module (also the front door to Mockup Tool v5). Reads the rate
 * card + blank price from data attributes on .bmn-epdp (all editable in theme
 * settings, nothing hard-coded), computes per-shirt and order totals with
 * application-based tiers (total units x positions) matching the BMN quote-builder,
 * plus one-off new-logo setup, and carries the full spec to cart
 * as line-item properties. Supply-later / free-mockup / WhatsApp route the spec
 * to the quote lead path (HubSpot). Prices are ex VAT; each money value is
 * rendered as dual .vat-inc/.vat-ex spans so the header VAT toggle switches them.
 */
(function () {
  'use strict';

  var COLOUR_MAP = {'black':'#1a1a1a','jet black':'#0d0d0d','white':'#ffffff','arctic white':'#ffffff','natural':'#efe7d6','ecru':'#efe7d6','navy':'#20294a','french navy':'#1b2440','oxford navy':'#1b2440','red':'#c0202a','classic red':'#c0202a','burgundy':'#6d1f2e','maroon':'#5e1a2b','royal':'#1f4fb0','royal blue':'#1f4fb0','sapphire':'#1e63a8','sky':'#7fb9e6','sky blue':'#7fb9e6','light blue':'#a9cde8','turquoise':'#2bb6c4','teal':'#1f8a8a','bottle green':'#0f3b2e','bottle':'#0f3b2e','forest':'#16432c','kelly':'#1f8f48','green':'#1f8f48','lime':'#9cc63b','olive':'#6b6a2f','sage':'#9bae8e','charcoal':'#3a3a3a','dark grey':'#4a4a4a','graphite':'#4a4a4a','heather grey':'#9b9b9b','grey':'#9b9b9b','gray':'#9b9b9b','sport grey':'#a8a8a8','light grey':'#cfcfcf','silver':'#c8c8c8','ash':'#d7d7d2','purple':'#5b3a8a','plum':'#5e2a4d','pink':'#e58fb0','light pink':'#f1c4d4','rose':'#d98aa0','coral':'#f08a6d','yellow':'#f4d03f','gold':'#e3b341','sunflower':'#f4c430','orange':'#e8732a','brown':'#5a3d2b'};

  var POSITIONS = [
    { id: 'lc', label: 'Left chest',   method: 'embroidery' },
    { id: 'rc', label: 'Right chest',  method: 'embroidery' },
    { id: 'cc', label: 'Centre chest', method: 'embroidery' },
    { id: 'ff', label: 'Full front',   method: 'dtf', oversized: true },
    { id: 'np', label: 'Nape / neck',  method: 'embroidery' },
    { id: 'ub', label: 'Upper back',   method: 'dtf' },
    { id: 'fb', label: 'Full back',    method: 'dtf', oversized: true },
    { id: 'ls', label: 'Left sleeve',  method: 'embroidery' },
    { id: 'rs', label: 'Right sleeve', method: 'embroidery' }
  ];

  // Tier by TOTAL APPLICATIONS (total units x number of positions), matching the
  // BMN quote-builder. Bands differ by method.
  function embBand(apps) { if (apps < 50) return 0; if (apps < 250) return 1; if (apps < 500) return 2; return 3; }        // 1-49 / 50-249 / 250-499 / 500+
  function dtfBand(apps) { if (apps < 9) return 0; if (apps < 25) return 1; if (apps < 100) return 2; if (apps < 250) return 3; if (apps < 500) return 4; return 5; } // 1-8 / 9-24 / 25-99 / 100-249 / 250-499 / 500+

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Names on each garment: one name per line, blanks dropped, each capped at 40 characters.
  function splitNames(v) { return String(v || '').split(/\r?\n/).map(function (x) { return x.trim().slice(0, 40); }).filter(Boolean); }
  // ---------- names and numbers on each garment (Anil, 1 Nov 2026) ----------
  // Back positions: football-shirt layout, name across the shoulders and a big number under it,
  // one row per garment tied to the sizes chosen. Front positions: one name per garment.
  var TEAM_POS = /^(ub|fb)$/;
  function isTeam(l) { return !!(l && l.source === 'text' && l.perName && TEAM_POS.test(l.id)); }
  function teamNeeds(l) { var m = isTeam(l) ? (l.teamMode || 'both') : 'name'; return { name: m !== 'number', num: m !== 'name' }; }
  var measureCtx = null;
  function textWidthEm(fontCss, t) {
    try { measureCtx = measureCtx || document.createElement('canvas').getContext('2d'); measureCtx.font = fontCss.replace('1em', '100px'); return measureCtx.measureText(t).width / 100; } catch (e) { return t.length * 0.55; }
  }
  var CAP = 0.72;   // capital height as a share of the font size
  function namesCountText(n, total) { return total > 0 ? (n + ' of ' + total + ' garment' + (total !== 1 ? 's' : '') + ' named') : (n + ' name' + (n !== 1 ? 's' : '') + ' added'); }

  function EnrichedPDP(root) {
    this.root = root;
    this.blank = parseInt(root.getAttribute('data-blank-price'), 10) || 0;          // pence, ex VAT
    this.sizeIndex = parseInt(root.getAttribute('data-size-index'), 10);
    this.colourIndex = parseInt(root.getAttribute('data-colour-index'), 10);
    this.setupFee = parseInt(root.getAttribute('data-setup-fee'), 10) || 0;         // pence
    this.setupFreeOver = parseInt(root.getAttribute('data-setup-free-over'), 10) || 0; // pence ex VAT, 0 = off
    this.setupFreeUnits = parseInt(root.getAttribute('data-setup-free-units'), 10) || 0; // items in the basket, 0 = off
    this.cartGarments = 0; this.cartHasSetup = false;
    // Waterproof garments: printed logos only, because embroidery needles pierce the waterproof layer.
    this.printOnly = root.getAttribute('data-print-only') === 'true';
    this.whatsapp = root.getAttribute('data-whatsapp') || '';
    this.leadUrl = root.getAttribute('data-lead-url') || '';
    this.setupVariantId = root.getAttribute('data-setup-variant') || '';
    // Branding charge: price in pence -> variant id of the hidden Branding product.
    try { this.brandingVariants = JSON.parse(root.getAttribute('data-branding-variants') || '{}'); } catch (e) { this.brandingVariants = {}; }
    this.title = root.getAttribute('data-product-title') || '';
    this.handle = root.getAttribute('data-product-handle') || '';
    // Visualiser v1: print-area template for this garment (assets/bmn-visualiser.js), or null.
    this.ptype = root.getAttribute('data-product-type') || '';
    // Real flat back photo (product metafield bmn.back_image), recoloured in the browser to the chosen colour.
    this.backPhoto = root.getAttribute('data-back-photo') || '';
    // assets/bmn-visualiser.js loads with the logo preview in the footer, after this script, so read it lazily.
    // Print areas set by hand for this product (metafield bmn.print_areas, from the setup tool), shares of the photo.
    try { this.areas = JSON.parse(root.getAttribute('data-print-areas') || 'null'); } catch (e) { this.areas = null; }
    if (this.areas && typeof this.areas !== 'object') this.areas = null;
    AREA_MM = areaMM(this.areas);
    var visSelf = this, visInfo;
    this._resetVis = function () { visInfo = undefined; };
    Object.defineProperty(this, 'vis', { get: function () {
      if (visInfo === undefined && window.BMNVis && window.BMNVis.setAreas) window.BMNVis.setAreas(visSelf.handle, visSelf.areas);
      if (visInfo === undefined && window.BMNVis) visInfo = window.BMNVis.info(visSelf.handle, visSelf.ptype, visSelf.title) || null;
      return visInfo || null;
    } });
    this.chestMM = 0;   // embroidered chest logo width already in the basket: one size per order
    this.repeat = false;
    this.express = false;
    this.expressVariantId = root.getAttribute('data-express-variant') || '';
    this.expressFee = toPence(root.getAttribute('data-express-fee'));
    this.expressDays = parseInt(root.getAttribute('data-express-days'), 10) || 5;
    // Builder v3 (pilot): step by step layout, several colours in one order, price ladder.
    this.v3 = root.getAttribute('data-v3') === 'true';
    this.mix = {};          // colour -> { size: { q, id } } for colours other than the one on screen
    this.ladderTab = null;
    try { this.rates = JSON.parse(root.getAttribute('data-rates') || '{}'); } catch (e) { this.rates = {}; }
    // rates in POUNDS from settings -> pence. Tiers keyed by total applications.
    this.ratesP = {
      embStd:  (this.rates.embStd  || [0,0,0,0]).map(toPence),
      embHigh: (this.rates.embHigh || [0,0,0,0]).map(toPence),
      dtf:     (this.rates.dtf     || [0,0,0,0,0,0]).map(toPence),
      oversizedEmb: toPence(this.rates.oversizedEmb),
      oversizedDtf: toPence(this.rates.oversizedDtf)
    };

    var vd = root.querySelector('.bmn-epdp__variant-data');
    this.variants = vd ? JSON.parse(vd.textContent) : [];
    this.variantMap = {};
    var self = this;
    this.variants.forEach(function (v) { self.variantMap[v.options.join('|')] = v; });

    // state
    this.colour = null;
    this.selected = {};   // id -> { method, source, text, fileName }

    this._cache();
    this._applyColourSwatches();
    this._bind();
    if (this.v3) this._v3Init();
    // default colour = first available swatch
    // Starting colour: the one in the link (?variant=), else Black, else the first. Chosen in Liquid.
    var initial = root.getAttribute('data-initial-colour');
    var firstSw = this.swatches.filter(function (s) { return s.getAttribute('data-value') === initial; })[0] || this.swatches[0];
    if (firstSw) this._selectColour(firstSw, { initial: true });
    this._resolveSizes();
    this._recompute();
    this._loadStock();
    this._loadCart();
  }

  // What is already in the basket: garments count towards the free setup, and designs already
  // in the basket are not charged again (one setup per design, Anil 3 Nov 2026).
  EnrichedPDP.prototype._loadCart = function () {
    var self = this;
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (c) {
      var g = 0, hasSetup = false, sv = String(self.setupVariantId || ''), designs = {};
      (c.items || []).forEach(function (it) {
        if (sv && String(it.variant_id) === sv) hasSetup = true;
        else if (it.product_type !== 'Branding') {
          g += it.quantity;
          var pr = it.properties || {};
          if (pr._spec && !/^none \(repeat/.test(pr.Setup || '')) {
            try { (JSON.parse(pr._spec).positions || []).forEach(function (p) { var k = designKey(p, it.product_title + '|' + (pr._Batch || '')); if (k) designs[k] = true; }); } catch (e) {}
          }
        }
        var ps = it.properties && it.properties['_Production spec'];
        if (ps) { try { var o = JSON.parse(ps); if (/^(lc|rc)$/.test(o.position) && o.method === 'embroidery' && o.width_mm) self.chestMM = o.width_mm; } catch (e) {} }
      });
      self.cartGarments = g; self.cartHasSetup = hasSetup; self.cartDesigns = designs;
      self._recompute();
    }).catch(function () {});
  };

  function toPence(v) { return Math.round((parseFloat(v) || 0) * 100); }

  // Digitising (Anil, 3 Nov 2026): one setup fee per embroidery design. A design is one artwork at
  // one size: the same file at two sizes is two designs, the same file and size on several garments
  // is one. Names and text are stitched from machine fonts and need no setup. Matches bmn-cart-rules.js.
  function designKey(p, owner) {
    if (!p || p.method !== 'embroidery' || p.source === 'text') return '';
    if (p.source === 'upload') return 'f:' + (p.fileName || '') + '|' + (p.mm || 0);
    return 'o:' + owner + '|' + p.id + '|' + (p.mm || 0);
  }

  EnrichedPDP.prototype._cache = function () {
    var r = this.root;
    this.swatches = Array.prototype.slice.call(r.querySelectorAll('.bmn-epdp__swatch'));
    var gal = r.querySelector('.bmn-epdp__gallery');
    this.galleryApi = gal && gal.bmnGallery ? gal.bmnGallery : null;
    this.productUrl = r.getAttribute('data-product-url') || '';
    this.variantTotal = parseInt(r.getAttribute('data-variant-total'), 10) || this.variants.length;
    this.colourLabel = r.querySelector('.bmn-epdp__colour-current');
    this.sizeRows = Array.prototype.slice.call(r.querySelectorAll('.bmn-epdp__size-row'));
    this.qtyInputs = Array.prototype.slice.call(r.querySelectorAll('.bmn-epdp__qty'));
    this.posButtons = Array.prototype.slice.call(r.querySelectorAll('.bmn-epdp__pos'));
    this.linesEl = r.querySelector('.bmn-epdp__lines');
    this.linesEmpty = r.querySelector('.bmn-epdp__lines-empty');
    this.elTotalUnits = r.querySelectorAll('.bmn-epdp__total-units');
    this.elPerShirt = r.querySelector('.bmn-epdp__per-shirt');
    this.elOrderTotal = r.querySelectorAll('.bmn-epdp__order-total');
    this.elSetup = r.querySelector('.bmn-epdp__setup-line');
    this.elTier = r.querySelector('.bmn-epdp__tier');
    this.elRec = r.querySelector('.bmn-epdp__rec');
    this.elBreak = r.querySelector('.bmn-epdp__breaks');
    this.addBtns = Array.prototype.slice.call(r.querySelectorAll('.bmn-epdp__add'));
    this.msg = r.querySelector('.bmn-epdp__msg');
    this.sticky = r.querySelector('.bmn-epdp__sticky');
    this.waBtn = r.querySelector('.bmn-epdp__whatsapp');
    this.mockupBtns = Array.prototype.slice.call(r.querySelectorAll('.bmn-epdp__mockup'));
    this.lead = r.querySelector('.bmn-epdp__lead');
    this.repeatToggle = r.querySelector('.bmn-epdp__repeat');
    this.expressToggle = r.querySelector('.bmn-epdp__express');
  };

  EnrichedPDP.prototype._applyColourSwatches = function () {
    this.swatches.forEach(function (sw) {
      if (sw.classList.contains('has-photo')) return; // the circle already shows a crop of the colour's own photo
      var n = (sw.getAttribute('data-value') || '').toLowerCase().trim();
      var hex = COLOUR_MAP[n];
      if (!hex) { for (var k in COLOUR_MAP) { if (n.indexOf(k) !== -1) { hex = COLOUR_MAP[k]; break; } } }
      if (hex) {
        sw.style.background = hex;
        if (hex === '#ffffff') sw.style.boxShadow = 'inset 0 0 0 1px rgba(15,15,15,0.25)';
      }
    });
  };

  EnrichedPDP.prototype._bind = function () {
    var self = this;
    var more = this.root.querySelector('.bmn-epdp__swatch-more');
    if (more) more.addEventListener('click', function () { more.parentNode.classList.remove('is-collapsed'); more.remove(); });
    var canHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    this.swatches.forEach(function (sw) {
      sw.addEventListener('click', function () { self._selectColour(sw); });
      if (canHover) {
        sw.addEventListener('mouseenter', function () { if (self.galleryApi && sw.getAttribute('data-value') !== self.colour) self.galleryApi.preview(self._colourView(sw)); });
        sw.addEventListener('mouseleave', function () { if (self.galleryApi) self.galleryApi.restore(); });
      }
    });
    this.qtyInputs.forEach(function (i) {
      i.addEventListener('input', function () { self._clamp(i); self._recompute(); self._clearMsg(); });
      i.addEventListener('change', function () { self._clamp(i); self._recompute(); });
    });
    this.root.querySelectorAll('.bmn-epdp__step').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var input = btn.parentNode.querySelector('.bmn-epdp__qty');
        var d = parseInt(btn.getAttribute('data-step'), 10) || 0;
        input.value = Math.max(0, (parseInt(input.value, 10) || 0) + d);
        self._clamp(input); self._recompute();
      });
    });
    this.posButtons.forEach(function (b) { b.addEventListener('click', function () { self._togglePosition(b.getAttribute('data-id')); }); });
    this.addBtns.forEach(function (b) { b.addEventListener('click', function () { self._addToCart(); }); });
    if (this.waBtn) this.waBtn.addEventListener('click', function () { self._whatsapp(); });
    this.mockupBtns.forEach(function (b) { b.addEventListener('click', function () { self._openLead('mockup'); }); });
    // delegated events on branding lines
    if (this.linesEl) {
      this.linesEl.addEventListener('click', function (e) {
        var mBtn = e.target.closest('.bmn-epdp__method-btn');
        if (mBtn) { self._setMethod(mBtn.getAttribute('data-pos'), mBtn.getAttribute('data-method')); return; }
        var rm = e.target.closest('.bmn-epdp__line-remove');
        if (rm) { self._togglePosition(rm.getAttribute('data-pos')); return; }
        var rv = e.target.closest('.bmn-epdp__rview');
        if (rv) { var sr = self.selected[rv.getAttribute('data-pos')]; if (sr) { sr.active = rv.getAttribute('data-key'); self._syncRoster(rv.getAttribute('data-pos')); self._viewFor(rv.getAttribute('data-pos')); Array.prototype.forEach.call(rv.closest('tbody').children, function (tr) { tr.classList.toggle('is-on', tr.getAttribute('data-key') === sr.active); }); self._backHtml = null; self._frontHtml = null; self._renderOverlay(self._orderedSelected()); } return; }
        var pb = e.target.closest('.bmn-epdp__paste-btn');
        if (pb) { var pw = self.linesEl.querySelector('.bmn-epdp__paste[data-pos="' + pb.getAttribute('data-pos') + '"]'); if (pw) { pw.hidden = !pw.hidden; if (!pw.hidden) pw.querySelector('textarea').focus(); } return; }
        var pg = e.target.closest('.bmn-epdp__paste-go');
        if (pg) { var ta0 = pg.parentNode.querySelector('textarea'); self._pasteRoster(pg.getAttribute('data-pos'), ta0 && ta0.value); return; }
        var tc = e.target.closest('.bmn-epdp__tcol');
        if (tc) { var st = self.selected[tc.getAttribute('data-pos')]; if (st) { st.tcolour = tc.getAttribute('data-colour'); self._viewFor(tc.getAttribute('data-pos')); self._renderLines(); self._recompute(); } return; }
      });
      this.linesEl.addEventListener('change', function (e) {
        var src = e.target.closest('.bmn-epdp__source');
        if (src) { self._viewFor(src.getAttribute('data-pos')); self._setSource(src.getAttribute('data-pos'), src.value); return; }
        var fnt = e.target.closest('.bmn-epdp__font');
        if (fnt) { var sf = self.selected[fnt.getAttribute('data-pos')]; if (sf) { sf.font = fnt.value; self._viewFor(fnt.getAttribute('data-pos')); self._renderLines(); self._recompute(); } return; }
        var pn = e.target.closest('.bmn-epdp__pername');
        if (pn) { var sp = self.selected[pn.getAttribute('data-pos')]; if (sp) { sp.perName = pn.checked; if (sp.perName && TEAM_POS.test(pn.getAttribute('data-pos'))) { sp.teamMode = sp.teamMode || 'both'; sp.nameH = sp.nameH || 60; sp.numH = sp.numH || 200; if (pn.getAttribute('data-pos') === 'fb' && !sp.oy) sp.oy = -40; } if (sp.perName && sp.roster === undefined && sp.text) { sp.roster = {}; var g0 = self._garments()[0]; if (g0) sp.roster[g0.key] = { name: sp.text, num: '' }; } if (sp.perName && !sp.names && sp.text) sp.names = sp.text; if (sp.perName) self._syncRoster(pn.getAttribute('data-pos')); self._renderLines(); self._recompute(); if (self.v3) self._renderOverlay(self._orderedSelected()); } return; }
        var tm = e.target.closest('.bmn-epdp__tmode, .bmn-epdp__tnh, .bmn-epdp__tnumh');
        if (tm) {
          var stm = self.selected[tm.getAttribute('data-pos')];
          if (stm) {
            if (tm.classList.contains('bmn-epdp__tmode')) stm.teamMode = tm.value;
            else { var cmv = parseFloat(String(tm.value).replace(',', '.')); if (cmv > 0) { var lo = +tm.min, hi2 = +tm.max; cmv = Math.max(lo, Math.min(hi2, cmv)); if (tm.classList.contains('bmn-epdp__tnh')) stm.nameH = cmv * 10; else stm.numH = cmv * 10; } }
            self._syncRoster(tm.getAttribute('data-pos')); self._viewFor(tm.getAttribute('data-pos')); self._renderLines(); self._recompute(); self._renderOverlay(self._orderedSelected());
          }
          return;
        }
        var hi = e.target.closest('.bmn-epdp__high');
        if (hi) { var sh = self.selected[hi.getAttribute('data-pos')]; if (sh) { sh.highStitch = hi.checked; self._renderLines(); self._recompute(); } return; }
        var ov = e.target.closest('.bmn-epdp__oversized');
        if (ov) { var so = self.selected[ov.getAttribute('data-pos')]; if (so) { so.oversized = ov.checked; self._renderLines(); self._recompute(); } return; }
        var file = e.target.closest('.bmn-epdp__file');
        if (file) {
          var s = self.selected[file.getAttribute('data-pos')];
          if (s) {
            self._viewFor(file.getAttribute('data-pos'));
            var f0 = file.files[0] || null;
            s.fileName = (f0 || {}).name || '';
            if (s.preview) { try { URL.revokeObjectURL(s.preview); } catch (e2) {} }
            s.preview = (self.v3 && f0 && /^image\/(png|jpe?g|gif|webp|svg\+xml)$/i.test(f0.type) && window.URL) ? URL.createObjectURL(f0) : '';
            self._renderLines(); self._recompute();
          }
          return;
        }
      });
      this._bindFlat();
      this.linesEl.addEventListener('input', function (e) {
        var txt = e.target.closest('.bmn-epdp__text');
        if (txt) { var s = self.selected[txt.getAttribute('data-pos')]; if (s) { s.text = txt.value; if (self.v3) self._renderOverlay(self._orderedSelected()); } return; }
        var nm = e.target.closest('.bmn-epdp__names');
        if (nm) { var sn = self.selected[nm.getAttribute('data-pos')]; if (sn) { sn.names = nm.value; sn.text = splitNames(nm.value)[0] || ''; var cEl = nm.parentNode.querySelector('.bmn-epdp__names-count'); if (cEl) cEl.textContent = namesCountText(splitNames(nm.value).length, self._totalUnits()); if (self.v3) self._renderOverlay(self._orderedSelected()); } return; }
        var rn = e.target.closest('.bmn-epdp__rname, .bmn-epdp__rnum');
        if (rn) {
          var srn = self.selected[rn.getAttribute('data-pos')];
          if (srn) {
            var key = rn.getAttribute('data-key'); srn.roster = srn.roster || {}; var ent = srn.roster[key] || (srn.roster[key] = { name: '', num: '' });
            if (rn.classList.contains('bmn-epdp__rnum')) { rn.value = rn.value.replace(/\D/g, '').slice(0, 3); ent.num = rn.value; } else ent.name = rn.value.slice(0, 20);
            srn.active = key; self._syncRoster(rn.getAttribute('data-pos'));
            var tb = rn.closest('tbody'); if (tb) Array.prototype.forEach.call(tb.children, function (tr) { tr.classList.toggle('is-on', tr.getAttribute('data-key') === key); });
            var lnr = self._orderedSelected().filter(function (x) { return x.id === rn.getAttribute('data-pos'); })[0], cnt = rn.closest('.bmn-epdp__artwork').querySelector('.bmn-epdp__names-count');
            if (cnt && lnr) { var rr = self._rosterRows(lnr), nd = teamNeeds(lnr); cnt.textContent = self._rosterFilled(lnr) + ' of ' + rr.length + ' garment' + (rr.length !== 1 ? 's' : '') + ' have ' + (nd.name && nd.num ? 'a name and number' : nd.num ? 'a number' : 'a name'); }
            self._viewFor(rn.getAttribute('data-pos')); self._backHtml = null; self._frontHtml = null; self._renderOverlay(self._orderedSelected());
            clearTimeout(self._rosterT); self._rosterT = setTimeout(function () { self._recompute(); }, 300);
          }
          return;
        }
        var sz = e.target.closest('.bmn-epdp__size');
        if (sz) {
          var ss = self.selected[sz.getAttribute('data-pos')];
          if (ss) {
            ss.mm = parseInt(sz.value, 10);
            var lab = sz.parentNode;
            var vEl = lab.querySelector('.bmn-epdp__size-val'), iEl = lab.querySelector('.bmn-epdp__size-in');
            if (vEl) vEl.textContent = ss.mm + 'mm';
            if (iEl) iEl.textContent = '(about ' + (ss.mm / 25.4).toFixed(1) + ' in)';
            self._syncFlat(sz.getAttribute('data-pos'));
            self._viewFor(sz.getAttribute('data-pos'));
            self._recompute();
          }
        }
      });
    }
    if (this.lead) {
      var form = this.lead.querySelector('form');
      if (form) form.addEventListener('submit', function (e) { e.preventDefault(); self._submitLead(); });
      var close = this.lead.querySelector('.bmn-epdp__lead-close');
      if (close) close.addEventListener('click', function () { self.lead.hidden = true; });
    }
    if (this.repeatToggle) this.repeatToggle.addEventListener('change', function () { self.repeat = self.repeatToggle.checked; self._recompute(); });
    if (this.expressToggle) this.expressToggle.addEventListener('change', function () { self.express = self.expressToggle.checked; self._recompute(); });

    var mainAdd = this.root.querySelector('.bmn-epdp__panel .bmn-epdp__add');
    if (mainAdd && this.sticky && 'IntersectionObserver' in window && this.root.classList.contains('bmn-epdp--v2')) {
      new IntersectionObserver(function (entries) {
        self._addInView = entries[entries.length - 1].isIntersecting;
        self.sticky.classList.toggle('is-ready', self._totalUnits() > 0 && !self._addInView);
      }).observe(mainAdd);
    }
  };

  EnrichedPDP.prototype._clamp = function (i) {
    var v = parseInt(i.value, 10); if (isNaN(v) || v < 0) v = 0; i.value = v;
    i.classList.toggle('is-active', v > 0);
  };

  // Supplier names sometimes carry a marker (e.g. "Black*"). Customers see the plain name.
  function displayName(v) { return String(v || '').replace(/\s*\*+\s*$/, '').trim(); }

  EnrichedPDP.prototype._colourView = function (sw) {
    var name = displayName(sw.getAttribute('data-value'));
    var pos = this.swatches.indexOf(sw) + 1, total = this.swatches.length;
    var img = sw.getAttribute('data-image');
    return {
      name: name,
      image: img,
      full: sw.getAttribute('data-full'),
      thumb: sw.getAttribute('data-thumb'),
      alt: this.title + ' in ' + name,
      meta: img ? 'Colour ' + pos + ' of ' + total + '. Tap a circle to see another colour.'
                : 'Colour ' + pos + ' of ' + total + '. Photo of this colour coming soon, call us for a swatch.'
    };
  };

  EnrichedPDP.prototype._selectColour = function (sw, opts) {
    opts = opts || {};
    if (this.v3 && this.colour != null && !opts.initial) {
      if (sw.getAttribute('data-value') === this.colour) return;
      this._saveColourQty(this.colour);
    }
    this.swatches.forEach(function (s) { s.classList.remove('is-selected'); s.setAttribute('aria-pressed', 'false'); });
    sw.classList.add('is-selected'); sw.setAttribute('aria-pressed', 'true');
    this.colour = sw.getAttribute('data-value');
    if (this.colourLabel) this.colourLabel.textContent = displayName(this.colour);
    var cp = this.root.querySelector('.bmn-epdp__colour-photo'), th = sw.getAttribute('data-thumb');
    if (cp) { if (th) { cp.src = th; cp.hidden = false; } else cp.hidden = true; }
    if (this.galleryApi) this.galleryApi.colour(this._colourView(sw));
    else this._setGalleryImage(sw.getAttribute('data-media-id'));
    this._ensureColourVariants(sw);
    this._resolveSizes();
    if (this.v3) this._restoreColourQty(this.colour);
    this._recompute();
    if (!opts.initial) this._updateUrl();
  };

  // Products with more than 250 variants: the page carries the first 250. The rest are fetched
  // one colour at a time from the bmn-variant-data section (Shopify's Section Rendering API).
  EnrichedPDP.prototype._hasColour = function (colour) {
    var ci = this.colourIndex;
    for (var i = 0; i < this.variants.length; i++) if (this.variants[i].options[ci] === colour) return true;
    return false;
  };
  EnrichedPDP.prototype._ensureColourVariants = function (sw) {
    var colour = sw.getAttribute('data-value'), id = sw.getAttribute('data-option-value-id');
    if (!id || !window.fetch || this.variants.length >= this.variantTotal || this._hasColour(colour)) return;
    this.loadingColour = colour;
    var self = this, url = (this.productUrl || window.location.pathname) + '?option_values=' + encodeURIComponent(id) + '&sections=bmn-variant-data';
    fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.json() : null; }).then(function (j) {
      var html = j && j['bmn-variant-data'];
      var m = html && html.match(/<script[^>]*bmn-variant-data[^>]*>([\s\S]*?)<\/script>/);
      var list = m ? JSON.parse(m[1]) : [];
      var have = {}; self.variants.forEach(function (v) { have[v.id] = true; });
      list.forEach(function (v) { if (!have[v.id]) { self.variants.push(v); self.variantMap[v.options.join('|')] = v; have[v.id] = true; } });
    }).catch(function () {}).then(function () {
      if (self.loadingColour === colour) self.loadingColour = null;
      if (self.colour === colour) { self._resolveSizes(); if (self.v3) self._restoreColourQty(colour); self._recompute(); self._updateUrl(); }
    });
  };

  // Keep the address bar on the chosen colour so a shared or bookmarked link opens on it.
  EnrichedPDP.prototype._updateUrl = function () {
    if (!window.history || !history.replaceState) return;
    var ci = this.colourIndex, colour = this.colour, v = null;
    for (var i = 0; i < this.variants.length; i++) if (this.variants[i].options[ci] === colour) { v = this.variants[i]; break; }
    if (!v) return;
    try { var u = new URL(window.location.href); u.searchParams.set('variant', v.id); history.replaceState(history.state, '', u.toString()); } catch (e) {}
  };

  EnrichedPDP.prototype._setGalleryImage = function (mediaId) {
    if (!mediaId) return;
    var gallery = document.querySelector('media-gallery'); if (!gallery) return;
    var slide = Array.prototype.slice.call(gallery.querySelectorAll('[data-media-id]')).find(function (el) { return el.dataset.mediaId && el.dataset.mediaId.split('-').pop() === String(mediaId); });
    if (slide && typeof gallery.setActiveMedia === 'function') { try { gallery.setActiveMedia(slide.dataset.mediaId, true); } catch (e) {} }
  };

  // Match a size row to a variant by constraining only the option slots that apply.
  // Works for one option (size only), two (size + colour) and three or more
  // (the extra options are left free and the first available match wins).
  EnrichedPDP.prototype._findVariant = function (size) {
    var n = this.variants.length ? this.variants[0].options.length : 0;
    var si = this.sizeIndex, ci = this.colourIndex;
    if (isNaN(si) || si < 0 || si >= n) si = 0;
    var useColour = !isNaN(ci) && ci >= 0 && ci < n && ci !== si && this.swatches.length > 0 && this.colour != null;
    var first = null;
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      if (v.options[si] !== size) continue;
      if (useColour && v.options[ci] !== this.colour) continue;
      if (v.available) return v;
      if (!first) first = v;
    }
    return first;
  };

  EnrichedPDP.prototype._resolveSizes = function () {
    var self = this;
    this.sizeRows.forEach(function (row) {
      var v = self._findVariant(row.getAttribute('data-size'));
      var input = row.querySelector('.bmn-epdp__qty');
      var badge = row.querySelector('.bmn-epdp__size-badge');
      if (v) {
        row.setAttribute('data-variant-id', v.id); input.setAttribute('data-variant-id', v.id);
        var live = (self.stock && v.sku) ? self.stock[String(v.sku).toUpperCase()] : null;
        // Once the stock service has written the live figures into Shopify, the live figure decides
        // whether a size can be ordered. Until then Shopify's own availability decides.
        var canOrder = (live && self.stockSynced) ? live.q > 0 : v.available;
        input.disabled = !canOrder; if (!canOrder) { input.value = 0; input.classList.remove('is-active'); }
        row.classList.toggle('is-unavailable', !canOrder);
        if (badge) badge.textContent = canOrder ? 'In stock' : 'Out of stock';
        row.classList.toggle('has-live', !!live);
        row.classList.toggle('is-live-in', !!live && live.q > 0);
        if (live && badge) { badge.textContent = self._stockText(live); badge.title = self._stockTitle(live); }
      } else {
        row.removeAttribute('data-variant-id'); input.removeAttribute('data-variant-id');
        input.disabled = true; input.value = 0; input.classList.remove('is-active');
        row.classList.add('is-unavailable'); row.classList.remove('has-live', 'is-live-in'); if (badge) badge.textContent = self.loadingColour === self.colour && self.colour ? 'Checking' : 'Not available';
      }
      Array.prototype.forEach.call(row.querySelectorAll('.bmn-epdp__step'), function (b) { b.disabled = input.disabled; });
    });
  };

  // Live stock. Asks the stock service once for this style. On any failure the page keeps the
  // figure it already has from Shopify and shows no live label.
  EnrichedPDP.prototype._loadStock = function () {
    var url = this.root.getAttribute('data-stock-url');
    var style = this.root.getAttribute('data-style');
    if (!url || !style || !window.fetch) return;
    var self = this;
    fetch(url + '?style=' + encodeURIComponent(style)).then(function (r) { return r.ok ? r.json() : null; }).then(function (d) {
      if (!d || !d.skus || !Object.keys(d.skus).length) return;
      self.stock = d.skus;
      self.stockSynced = d.synced === true;
      self._resolveSizes();
      self._recompute();
      var note = self.root.querySelector('.bmn-epdp__stock-note');
      if (note) { note.textContent = 'Live stock, updated within the last 30 minutes.'; note.hidden = false; }
    }).catch(function () {});
  };
  EnrichedPDP.prototype._leadDays = function (live) {
    var m = live && live.more > 0 && live.lead ? String(live.lead).match(/(\d+)/) : null;
    return m ? parseInt(m[1], 10) : 0;
  };
  EnrichedPDP.prototype._stockText = function (live) {
    if (live.q > 0) return (live.q >= 100 ? '100+' : live.q) + ' in stock';
    var days = this._leadDays(live);
    if (days) return 'Due in about ' + days + ' day' + (days === 1 ? '' : 's');
    return 'Out of stock';
  };
  EnrichedPDP.prototype._stockTitle = function (live) {
    var days = this._leadDays(live);
    if (live.q > 0) return live.q + ' available now' + (days ? '. More due in about ' + days + ' day' + (days === 1 ? '' : 's') + ' (estimate).' : '.');
    return days ? 'Out of stock. More due in about ' + days + ' day' + (days === 1 ? '' : 's') + ' (estimate).' : 'Out of stock.';
  };

  EnrichedPDP.prototype._togglePosition = function (id) {
    if (this.selected[id]) { delete this.selected[id]; }
    else {
      var def = POSITIONS.filter(function (p) { return p.id === id; })[0] || { method: 'embroidery' };
      this.selected[id] = { method: this.printOnly ? 'dtf' : def.method, source: 'later', text: '', fileName: '', highStitch: false, oversized: !!def.oversized, perName: false, names: '', font: 'block', tcolour: this.v3 ? this._defaultInk() : '', mm: (SIZES[id] || [0, 0, 0, 0])[3] };
      var nv = this.selected[id];
      if (this.vis && !this.printOnly) {
        if (this.vis.embOnly) nv.method = 'embroidery';
        else if (this.vis.def === 'dtf' && /^(lc|rc|cc|ff|ls|rs)$/.test(id)) nv.method = 'dtf';
      }
      if (this.chestMM && /^(lc|rc)$/.test(id) && nv.method === 'embroidery') nv.mm = this.chestMM;
      if (this.vis && this.vis.excl && this.vis.excl[id]) {
        var clash = this.vis.excl[id].filter(function (o) { return this.selected[o]; }, this);
        if (clash.length) { clash.forEach(function (o) { delete this.selected[o]; }, this); var nm = function (k) { var p = POSITIONS.filter(function (x) { return x.id === k; })[0]; return p ? p.label : k; }; this._say(nm(id) + ' and ' + clash.map(nm).join(' and ') + ' overlap on this garment, so we kept the one you just picked.', 'success'); }
      }
      if (this.v3) this._viewFor(id);
    }
    this.posButtons.forEach(function (b) {
      var on = b.getAttribute('data-id') in this.selected;
      b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    }, this);
    this._renderLines();
    this._recompute();
  };

  EnrichedPDP.prototype._setMethod = function (id, method) {
    if (this.printOnly) method = 'dtf';
    if (this.vis && this.vis.embOnly) method = 'embroidery';
    if (this.selected[id]) { this.selected[id].method = method; this._renderLines(); this._recompute(); }
  };
  EnrichedPDP.prototype._setSource = function (id, source) {
    if (this.selected[id]) { this.selected[id].source = source; this._renderLines(); this._recompute(); }
  };

  EnrichedPDP.prototype._orderedSelected = function () {
    var self = this;
    return POSITIONS.filter(function (p) { return self.selected[p.id]; }).map(function (p) {
      var s = self.selected[p.id];
      return { id: p.id, label: (self.vis && self.vis.labels && self.vis.labels[p.id]) || p.label, method: s.method, source: s.source, text: s.text, fileName: s.fileName, highStitch: !!s.highStitch, oversized: !!s.oversized, preview: s.preview || '', font: s.font || 'block', tcolour: s.tcolour || '', mm: s.mm || 0, perName: !!s.perName, names: s.names || '', rot: s.rot || 0, roster: s.roster || {}, teamMode: s.teamMode || 'both', nameH: s.nameH || 60, numH: s.numH || 200, active: s.active || '', fx: s.fx, fy: s.fy, ox: s.ox || 0, oy: s.oy || 0 };
    });
  };

  // total applications = total units x number of decoration positions
  EnrichedPDP.prototype._apps = function () { return this._totalUnits() * this._orderedSelected().length; };

  // per-position rate (pence) at a given total-applications count
  // Logo width in mm as made: the flat print-area width on a measured photo, otherwise the chosen size.
  EnrichedPDP.prototype._widthFor = function (l) {
    if (!l || !l.id || !SIZES[l.id]) return 0;
    if (isTeam(l)) return this._teamBox(l).w;
    if ((SPOTS[l.id] || {}).v === 'front' && this._onPhoto(l) && window.BMNVis && window.BMNVis.areaDims) {
      var ad = this._areaFor(l.id); if (ad && ad.w) return this._flatPos(l, ad.w, ad.h).lw;
    }
    return sizeOf(l);
  };
  // Price by size (Anil, 1 Nov 2026). Embroidery: up to 100mm standard, up to 150mm large, bigger is the
  // extra large flat rate. Print: up to 150mm standard, bigger costs the large print rate (never less than standard).
  var SIZE_STD = { embroidery: 100, dtf: 150 }, SIZE_LARGE = 150;
  EnrichedPDP.prototype._sizeBand = function (l) {
    if (!this.v3 || !l || !l.id || !SIZES[l.id] || (this.vis && HEADWEAR.test(this.vis.tpl))) return null;
    var w = this._widthFor(l); if (!w) return null;
    if (l.method === 'dtf') return w > SIZE_STD.dtf ? 'large' : 'std';
    return w > SIZE_LARGE ? 'xl' : w > SIZE_STD.embroidery ? 'large' : 'std';
  };
  EnrichedPDP.prototype._rateForLine = function (l, apps) {
    var r = this.ratesP, band = this._sizeBand(l);
    if (band) {
      if (l.method === 'dtf') { var d = r.dtf[dtfBand(apps)] || 0; return band === 'large' ? Math.max(d, r.oversizedDtf) : d; }
      var hi = r.embHigh[embBand(apps)] || 0;
      return band === 'xl' ? Math.max(hi, r.oversizedEmb) : band === 'large' ? hi : (r.embStd[embBand(apps)] || 0);
    }
    if (l.oversized) return l.method === 'dtf' ? r.oversizedDtf : r.oversizedEmb;
    if (l.method === 'dtf') return r.dtf[dtfBand(apps)] || 0;
    return (l.highStitch ? r.embHigh : r.embStd)[embBand(apps)] || 0;
  };

  EnrichedPDP.prototype._textInput = function (l) {
    var box = '<label class="bmn-epdp__flag bmn-epdp__pername-wrap"><input type="checkbox" class="bmn-epdp__pername" data-pos="' + l.id + '"' + (l.perName ? ' checked' : '') + '> ' + (TEAM_POS.test(l.id) ? 'Names and numbers, different on each garment' : 'Different name on each garment') + '</label>';
    if (l.perName && this.v3) return box + this._rosterHtml(l);
    if (!l.perName) return '<input type="text" class="bmn-epdp__text" data-pos="' + l.id + '" value="' + esc(l.text) + '" placeholder="Name or text to decorate" maxlength="40" />' + box;
    return box + '<textarea class="bmn-epdp__names" data-pos="' + l.id + '" rows="4" placeholder="One name per line. If sizes differ, add the size after each name." aria-label="Names for ' + esc(l.label) + ', one per garment">' + esc(l.names) + '</textarea>' +
      '<p class="bmn-epdp__names-count" aria-live="polite">' + namesCountText(splitNames(l.names).length, this._totalUnits()) + '</p>';
  };

  EnrichedPDP.prototype._renderLines = function () {
    if (!this.linesEl) return;
    var lines = this._orderedSelected();
    if (this.linesEmpty) this.linesEmpty.hidden = lines.length > 0;
    var apps = this._apps();
    var self = this;
    this.linesEl.innerHTML = lines.map(function (l) {
      var uplift = self._rateForLine(l, apps);
      var flags = '';
      if (l.method === 'embroidery' && !l.oversized) {
        flags += '<label class="bmn-epdp__flag"><input type="checkbox" class="bmn-epdp__high" data-pos="' + l.id + '"' + (l.highStitch ? ' checked' : '') + '> Large / detailed logo (high stitch)</label>';
      }
      flags += '<label class="bmn-epdp__flag"><input type="checkbox" class="bmn-epdp__oversized" data-pos="' + l.id + '"' + (l.oversized ? ' checked' : '') + '> Oversized / big back (flat rate)</label>';
      // Builder v3: no stitch or size boxes for the buyer to guess at. Embroidery is priced as a
      // standard logo; a full back uses the big back price. Anything extra shows on the free mockup.
      if (self.v3) {
        var sized = !!self._sizeBand(l);
        flags = '<p class="bmn-epdp__line-note">' + (sized
          ? (l.method === 'dtf' ? 'Printed: full colour, soft and flexible, no setup charge. Priced by size: up to 15cm wide is standard, bigger is a large print.' : 'Embroidered: stitched, raised and hard-wearing. Priced by size: up to 10cm wide is standard, up to 15cm is large, bigger is extra large.')
          : l.oversized
          ? (l.method === 'dtf' ? 'Large back print, flat price.' : 'Large back embroidery, flat price.')
          : (l.method === 'dtf' ? 'Printed: full colour and fine detail, soft and flexible, no setup charge.' : 'Embroidered: stitched, raised and hard-wearing' + (/^(lc|rc|cc)$/.test(l.id) && !(self.vis && HEADWEAR.test(self.vis.tpl)) ? ', the classic look for chest logos' : '') + '. Priced as a standard logo.')) +
          (l.method === 'embroidery' ? ' If your logo needs extra stitches, any difference shows on your free mockup and nothing is made until you approve it.' : ' Your free mockup confirms the size before anything is made.') + '</p>';
      }
      if (self.vis && l.method === 'dtf' && /^(lc|rc|cc|ff|ls|rs)$/.test(l.id)) {
        if (self.vis.noDtf) flags += '<p class="bmn-epdp__line-note">Print is not recommended on fleece. Embroidery gives the better finish.</p>';
        else if (self.vis.note) flags += '<p class="bmn-epdp__line-note">' + esc(self.vis.note) + '</p>';
      }
      return '' +
        '<div class="bmn-epdp__line" data-pos="' + l.id + '">' +
          '<div class="bmn-epdp__line-head">' +
            '<strong>' + esc(l.label) + '</strong>' +
            '<button type="button" class="bmn-epdp__line-remove" data-pos="' + l.id + '" aria-label="Remove ' + esc(l.label) + '">Remove</button>' +
          '</div>' +
          '<div class="bmn-epdp__line-row">' +
            (self.printOnly
              ? '<span class="bmn-epdp__method-fixed">Printed logo</span>'
              : (self.vis && self.vis.embOnly) ? '<span class="bmn-epdp__method-fixed">Embroidered logo</span>'
              : '<div class="bmn-epdp__method" role="group" aria-label="Decoration method">' +
                  '<button type="button" class="bmn-epdp__method-btn' + (l.method === 'embroidery' ? ' is-on' : '') + '" data-pos="' + l.id + '" data-method="embroidery">Embroidery</button>' +
                  '<button type="button" class="bmn-epdp__method-btn' + (l.method === 'dtf' ? ' is-on' : '') + '" data-pos="' + l.id + '" data-method="dtf">DTF print</button>' +
                '</div>') +
            '<span class="bmn-epdp__line-uplift">+ ' + self._dual(uplift) + ' <small>each</small></span>' +
          '</div>' +
          '<div class="bmn-epdp__flags">' + flags + '</div>' +
          '<div class="bmn-epdp__artwork">' +
            '<select class="bmn-epdp__source" data-pos="' + l.id + '" aria-label="Artwork for ' + esc(l.label) + '">' +
              '<option value="later"' + (l.source === 'later' ? ' selected' : '') + '>Supply logo later (free mockup)</option>' +
              '<option value="upload"' + (l.source === 'upload' ? ' selected' : '') + '>Upload logo now</option>' +
              '<option value="text"' + (l.source === 'text' ? ' selected' : '') + '>Add text / name</option>' +
              (self.v3 ? '<option value="redraw"' + (l.source === 'redraw' ? ' selected' : '') + '>No usable logo? We redraw it (from £25 ex VAT)</option>' : '') +
            '</select>' +
            // Each position has its own artwork (Printful style): thumbnail, file name and a button to use a different logo here.
            (l.source === 'upload' ? '<div class="bmn-epdp__art">' + (l.preview ? '<span class="bmn-epdp__art-thumb" style="background-image:url(&quot;' + esc(l.preview) + '&quot;)"></span>' : '<span class="bmn-epdp__art-thumb is-empty"></span>') + '<span class="bmn-epdp__art-name">' + (l.fileName ? esc(l.fileName) : 'No file yet') + '</span><label class="bmn-epdp__file-wrap bmn-epdp__art-swap"><input type="file" class="bmn-epdp__file" data-pos="' + l.id + '" accept="image/*,.pdf,.ai,.eps,.svg" /><span>' + (l.fileName ? 'Use a different logo here' : 'Choose file') + '</span></label></div>' : '') +
            (l.source === 'text' ? self._textInput(l) : '') +
            (self.v3 && l.source === 'text' ? self._textControls(l) : '') +
            (self.v3 && SIZES[l.id] && !isTeam(l) ? self._sizeControl(l) : '') +
            (self.v3 ? self._flatControl(l) : '') +
            (l.source === 'redraw' ? '<p class="bmn-epdp__line-note">Send us whatever you have, even a photo. We rebuild it properly: £25 for simple type, £45 for a logo with text, more complex logos quoted. Free if you have a vector file (AI, EPS, SVG or PDF). The price is confirmed on your free mockup before anything is made, and the file is yours for every reorder.</p>' : '') +
          '</div>' +
        '</div>';
    }).join('');
  };

  // Every garment in the order, in size order: [{ key, colour, size }]. Keys stay put as quantities change.
  EnrichedPDP.prototype._garments = function () {
    var self = this, order = this.sizeRows.map(function (r) { return r.getAttribute('data-size'); }), out = [];
    function add(colour, size, q) { for (var i = 0; i < q; i++) out.push({ key: colour + '|' + size + '|' + i, colour: colour, size: size }); }
    this.sizeRows.forEach(function (row) { var i = row.querySelector('.bmn-epdp__qty'); var q = i.disabled ? 0 : (parseInt(i.value, 10) || 0); if (q > 0) add(self.colour, row.getAttribute('data-size'), q); });
    Object.keys(this.mix || {}).forEach(function (c) {
      if (c === self.colour) return;
      Object.keys(self.mix[c]).sort(function (a, b) { return order.indexOf(a) - order.indexOf(b); }).forEach(function (sz) { add(c, sz, self.mix[c][sz].q); });
    });
    return out;
  };
  // Roster rows for a line, one per garment, with what has been typed so far.
  EnrichedPDP.prototype._rosterRows = function (l) {
    var r = l.roster || {}, multi = Object.keys(this.mix || {}).some(function (c) { return c !== this.colour; }, this);
    return this._garments().map(function (g) { var e = r[g.key] || {}; return { key: g.key, label: (multi ? displayName(g.colour) + ' ' : '') + g.size, size: g.size, colour: g.colour, name: e.name || '', num: e.num || '' }; });
  };
  EnrichedPDP.prototype._rosterFilled = function (l) {
    var need = teamNeeds(l);
    return this._rosterRows(l).filter(function (w) { return (!need.name || w.name.trim()) && (!need.num || String(w.num).trim()); }).length;
  };
  // Keep the old one-name-per-line fields in step with the roster (validation, order text).
  EnrichedPDP.prototype._syncRoster = function (id) {
    var s = this.selected[id]; if (!s) return;
    var l = this._orderedSelected().filter(function (x) { return x.id === id; })[0]; if (!l) return;
    var rows = this._rosterRows(l), act = rows.filter(function (w) { return w.key === s.active; })[0] || rows.filter(function (w) { return w.name || w.num; })[0];
    s.names = rows.map(function (w) { return w.name; }).filter(Boolean).join('\n');
    s.text = act ? ((act.name || '') + (act.num ? ' ' + act.num : '')).trim() : '';
    if (isTeam(l)) s.mm = Math.round(this._teamBox(l).w);
  };
  // Name and number block in mm, sized by the widest name and number on the roster.
  EnrichedPDP.prototype._teamBox = function (l, row) {
    var f = FONTS[l.font] || FONTS.block, need = teamNeeds(l), rows = this._rosterRows(l);
    var up = function (t) { return f.upper ? String(t).toUpperCase() : String(t); };
    var names = rows.map(function (w) { return up(w.name); }).filter(Boolean), nums = rows.map(function (w) { return String(w.num); }).filter(Boolean);
    if (!names.length) names = ['YOUR NAME']; if (!nums.length) nums = ['10'];
    var nameH = need.name ? (l.nameH || 60) : 0, numH = need.num ? (l.numH || 200) : 0;
    var wName = 0, wNum = 0;
    names.forEach(function (t) { wName = Math.max(wName, textWidthEm(f.css, t) * nameH / CAP); });
    nums.forEach(function (t) { wNum = Math.max(wNum, textWidthEm(f.css, t) * numH / CAP); });
    var nameScale = need.name && wName > 300 ? 300 / wName : 1;   // long names shrink to fit 30cm
    var gap = need.name && need.num ? Math.max(15, nameH * 0.35) : 0;
    var w = Math.max(need.name ? Math.min(wName, 300) : 0, need.num ? wNum : 0, 20), h = nameH + gap + numH;
    var act = row || rows.filter(function (x) { return x.key === l.active; })[0] || rows.filter(function (x) { return x.name || x.num; })[0];
    return { w: w, h: h, nameH: nameH, numH: numH, gap: gap, nameScale: nameScale, need: need,
      name: act && act.name ? up(act.name) : (rows.some(function (x) { return x.name; }) ? '' : 'YOUR NAME'),
      num: act && act.num ? String(act.num) : (rows.some(function (x) { return x.num; }) ? '' : '10'), placeholder: !act };
  };
  EnrichedPDP.prototype._teamSvg = function (l, b) {
    var f = FONTS[l.font] || FONTS.block, ink = inkHex(l.tcolour || this._defaultInk()), st = l.tcolour === 'white' ? ' stroke="rgba(0,0,0,.25)" stroke-width="' + (b.h / 300).toFixed(2) + '"' : '';
    var op = b.placeholder ? ' opacity=".55"' : '';
    var t = '';
    if (b.need.name && b.name) t += '<text x="' + (b.w / 2) + '" y="' + b.nameH + '" text-anchor="middle" style="font:' + f.css + ';font-size:' + (b.nameH / CAP * b.nameScale).toFixed(1) + 'px" fill="' + ink + '"' + st + op + '>' + esc(b.name) + '</text>';
    if (b.need.num && b.num) t += '<text x="' + (b.w / 2) + '" y="' + b.h + '" text-anchor="middle" style="font:' + f.css + ';font-size:' + (b.numH / CAP).toFixed(1) + 'px" fill="' + ink + '"' + st + op + '>' + esc(b.num) + '</text>';
    return '<svg viewBox="0 0 ' + b.w.toFixed(1) + ' ' + b.h.toFixed(1) + '" preserveAspectRatio="xMidYMid meet" style="width:100%;height:100%;overflow:visible">' + t + '</svg>';
  };
  EnrichedPDP.prototype._rosterHtml = function (l) {
    var rows = this._rosterRows(l), team = isTeam(l), need = teamNeeds(l), self = this;
    var head = team
      ? '<div class="bmn-epdp__team-opts"><label>Print <select class="bmn-epdp__tmode" data-pos="' + l.id + '"><option value="both"' + (l.teamMode === 'both' ? ' selected' : '') + '>Name and number</option><option value="name"' + (l.teamMode === 'name' ? ' selected' : '') + '>Name only</option><option value="number"' + (l.teamMode === 'number' ? ' selected' : '') + '>Number only</option></select></label>' +
        (need.name ? '<label>Name height <input type="number" class="bmn-epdp__tnh" data-pos="' + l.id + '" min="3" max="10" step="0.5" value="' + ((l.nameH || 60) / 10) + '"> cm</label>' : '') +
        (need.num ? '<label>Number height <input type="number" class="bmn-epdp__tnumh" data-pos="' + l.id + '" min="8" max="30" step="1" value="' + ((l.numH || 200) / 10) + '"> cm</label>' : '') + '</div>'
      : '';
    if (!rows.length) return head + '<p class="bmn-epdp__line-note">Pick your sizes above. A row appears here for each garment.</p>';
    var body = rows.map(function (w) {
      return '<tr data-key="' + esc(w.key) + '"' + (w.key === l.active ? ' class="is-on"' : '') + '><td>' + esc(w.label) + '</td>' +
        (need.name ? '<td><input type="text" class="bmn-epdp__rname" data-pos="' + l.id + '" data-key="' + esc(w.key) + '" value="' + esc(w.name) + '" placeholder="Name" maxlength="20" aria-label="Name for ' + esc(w.label) + '"></td>' : '') +
        (need.num ? '<td><input type="text" class="bmn-epdp__rnum" data-pos="' + l.id + '" data-key="' + esc(w.key) + '" value="' + esc(w.num) + '" placeholder="No." maxlength="3" inputmode="numeric" aria-label="Number for ' + esc(w.label) + '"></td>' : '') +
        '<td><button type="button" class="bmn-epdp__rview" data-pos="' + l.id + '" data-key="' + esc(w.key) + '">View</button></td></tr>';
    }).join('');
    var what = need.name && need.num ? 'a name and number' : need.num ? 'a number' : 'a name';
    return head + '<table class="bmn-epdp__roster"><thead><tr><th>Garment</th>' + (need.name ? '<th>Name</th>' : '') + (need.num ? '<th>No.</th>' : '') + '<th></th></tr></thead><tbody>' + body + '</tbody></table>' +
      '<p class="bmn-epdp__names-count" aria-live="polite">' + this._rosterFilled(l) + ' of ' + rows.length + ' garment' + (rows.length !== 1 ? 's' : '') + ' have ' + what + '</p>' +
      '<button type="button" class="bmn-epdp__paste-btn" data-pos="' + l.id + '">Paste a list</button>' +
      '<div class="bmn-epdp__paste" data-pos="' + l.id + '" hidden><textarea class="bmn-epdp__paste-ta" rows="4" placeholder="One per line, straight from a spreadsheet: name, number, size. Example: Smith, 7, M"></textarea><button type="button" class="bmn-epdp__paste-go" data-pos="' + l.id + '">Fill the rows</button></div>';
  };
  // Paste: "name, number, size" per line (tabs or commas). Sized lines go to that size first.
  EnrichedPDP.prototype._pasteRoster = function (id, text) {
    var s = this.selected[id]; if (!s) return;
    var l = this._orderedSelected().filter(function (x) { return x.id === id; })[0], rows = this._rosterRows(l), need = teamNeeds(l);
    s.roster = s.roster || {};
    var used = {}; rows.forEach(function (w) { if (w.name || w.num) used[w.key] = 1; });
    String(text || '').split(/\r?\n/).forEach(function (line) {
      var parts = line.split(/\t|,|;/).map(function (x) { return x.trim(); }).filter(Boolean); if (!parts.length) return;
      // "White 11" or "11 White" on one cell: split the number off
      parts = parts.reduce(function (a, x) { var m1 = x.match(/^(.*\D)\s+(\d{1,3})$/), m2 = x.match(/^(\d{1,3})\s+(\D.*)$/); if (m1) a.push(m1[1].trim(), m1[2]); else if (m2) a.push(m2[1], m2[2].trim()); else a.push(x); return a; }, []);
      var size = null, num = '', names = [];
      parts.forEach(function (p) {
        if (!size && rows.some(function (w) { return w.size.toUpperCase() === p.toUpperCase(); })) size = p.toUpperCase();
        else if (!num && /^\d{1,3}$/.test(p)) num = p;
        else names.push(p);
      });
      var name = names.join(' ').slice(0, 20);
      if (!name && !num) return;
      var target = rows.filter(function (w) { return !used[w.key] && (!size || w.size.toUpperCase() === size); })[0] || rows.filter(function (w) { return !used[w.key]; })[0];
      if (!target) return;
      used[target.key] = 1;
      s.roster[target.key] = { name: need.name ? name : '', num: need.num ? num : '' };
    });
    this._syncRoster(id); this._renderLines(); this._recompute(); this._renderOverlay(this._orderedSelected());
  };

  // "M: SMITH 7 | L: JONES 10" for the order.
  EnrichedPDP.prototype._rosterText = function (l, sep) {
    var need = teamNeeds(l), f = FONTS[l.font] || FONTS.block;
    return this._rosterRows(l).map(function (w) { var n = need.name ? (f.upper ? w.name.toUpperCase() : w.name) : ''; return w.label + ': ' + (n + (need.num ? ' ' + (w.num ? '#' + w.num : '') : '')).trim(); }).join(sep || ' | ');
  };
  EnrichedPDP.prototype._currentUnits = function () {
    return this.qtyInputs.reduce(function (s, i) { return s + (i.disabled ? 0 : (parseInt(i.value, 10) || 0)); }, 0);
  };
  // Units in the other colours already chosen (builder v3). Zero on the classic builder.
  EnrichedPDP.prototype._otherUnits = function () {
    var n = 0, mix = this.mix || {};
    Object.keys(mix).forEach(function (c) { Object.keys(mix[c]).forEach(function (sz) { n += mix[c][sz].q; }); });
    return n;
  };
  EnrichedPDP.prototype._totalUnits = function () { return this._currentUnits() + this._otherUnits(); };

  // ---------- builder v3: several colours in one order ----------
  EnrichedPDP.prototype._saveColourQty = function (colour) {
    var m = {};
    this.sizeRows.forEach(function (row) {
      var i = row.querySelector('.bmn-epdp__qty');
      var q = i.disabled ? 0 : (parseInt(i.value, 10) || 0);
      if (q > 0 && i.getAttribute('data-variant-id')) m[row.getAttribute('data-size')] = { q: q, id: parseInt(i.getAttribute('data-variant-id'), 10) };
    });
    // Keep sizes still waiting for this colour's variants to load
    var old = this.mix[colour];
    if (old) Object.keys(old).forEach(function (sz) { if (!m[sz] && old[sz].pending) m[sz] = old[sz]; });
    if (Object.keys(m).length) this.mix[colour] = m; else delete this.mix[colour];
  };
  EnrichedPDP.prototype._restoreColourQty = function (colour) {
    var m = this.mix[colour], self = this, keep = {};
    this.sizeRows.forEach(function (row) {
      var i = row.querySelector('.bmn-epdp__qty'), sz = row.getAttribute('data-size');
      var e = m && m[sz];
      if (e && !i.disabled) { i.value = e.q; }
      else if (e && self.loadingColour === colour) { keep[sz] = { q: e.q, id: e.id, pending: true }; i.value = 0; }
      else { i.value = 0; }
      self._clamp(i);
    });
    if (Object.keys(keep).length) this.mix[colour] = keep; else delete this.mix[colour];
  };
  // [{ colour, units, sizes: 'S x2, M x3', items: [{ id, quantity }] }] for every colour with units
  EnrichedPDP.prototype._colourOrders = function () {
    var out = [], self = this;
    var cur = { colour: this.colour, units: 0, parts: [], items: [] };
    this.sizeRows.forEach(function (row) {
      var i = row.querySelector('.bmn-epdp__qty');
      var q = i.disabled ? 0 : (parseInt(i.value, 10) || 0);
      if (q > 0 && i.getAttribute('data-variant-id')) {
        cur.units += q; cur.parts.push(row.getAttribute('data-size') + ' x' + q);
        cur.items.push({ id: parseInt(i.getAttribute('data-variant-id'), 10), quantity: q });
      }
    });
    if (cur.units) out.push(cur);
    Object.keys(this.mix || {}).forEach(function (c) {
      var o = { colour: c, units: 0, parts: [], items: [] };
      self.sizeRows.forEach(function (row) {
        var sz = row.getAttribute('data-size'), e = self.mix[c][sz];
        if (e && e.q > 0 && e.id) { o.units += e.q; o.parts.push(sz + ' x' + e.q); o.items.push({ id: e.id, quantity: e.q }); }
      });
      if (o.units) out.push(o);
    });
    out.forEach(function (o) { o.sizes = o.parts.join(', '); });
    return out;
  };

  // Offer: free digitisation when the order (garments and decoration, ex VAT, before setup) is over the threshold.
  EnrichedPDP.prototype._expressOn = function (lines) {
    return !!(this.express && this.expressFee && this.expressVariantId && lines && lines.length);
  };

  EnrichedPDP.prototype._setupRuleText = function () {
    var a = [];
    if (this.setupFreeUnits) a.push(this.setupFreeUnits + '+ items');
    if (this.setupFreeOver) a.push('orders over ' + this._plain(this.setupFreeOver).replace(/\.00$/, '') + ' ex VAT');
    return a.join(' or ');
  };

  // The free-setup offer (N+ items, or over the threshold) covers the FIRST design in the order only.
  EnrichedPDP.prototype._setupOffer = function (subtotal) {
    if (this.setupFreeOver > 0 && subtotal > this.setupFreeOver) return true;
    return this.setupFreeUnits > 0 && (this._totalUnits() + (this.cartGarments || 0)) >= this.setupFreeUnits;
  };

  // How many setup fees this product adds, given what is already in the basket.
  // Returns { count, designs (new on this page), inBasket, offer }.
  EnrichedPDP.prototype._setupInfo = function (lines, subtotal) {
    var cart = this.cartDesigns || {}, cartN = Object.keys(cart).length, seen = {}, fresh = 0, self = this;
    (lines || []).forEach(function (l) {
      var k = designKey(l, 'page|' + self.handle);
      if (!k || seen[k]) return; seen[k] = true;
      if (!cart[k]) fresh++;
    });
    var offer = this.setupFee ? this._setupOffer(subtotal) : false;
    var free = offer ? 1 : 0;
    var count = (!this.setupFee || this.repeat) ? 0 : Math.max(0, cartN + fresh - free) - Math.max(0, cartN - free);
    return { count: count, designs: fresh, inBasket: Object.keys(seen).length - fresh, offer: offer };
  };

  // Kept for any older callers: true when this product adds no setup fee.
  EnrichedPDP.prototype._setupWaived = function (subtotal) {
    return this._setupInfo(this._orderedSelected(), subtotal).count === 0;
  };

  EnrichedPDP.prototype._setupNote = function (info) {
    var fee = this._plain(this.setupFee), rule = this._setupRuleText();
    var offerText = rule ? ' First design free on ' + rule + '.' : '';
    if (info.count > 0) {
      var n = info.designs + info.inBasket;
      return 'Logo setup (digitising): ' + fee + ' per design' + (n > 1 ? ', ' + info.count + ' charged (' + this._plain(this.setupFee * info.count) + ')' : '') + '.' + offerText + ' Repeat logos are free.';
    }
    if (info.designs === 0 && info.inBasket > 0) return 'Logo setup: this design is already in your basket.';
    return 'Logo setup: first design free on ' + (rule || 'this order') + '. ' + fee + ' per extra design or size.';
  };

  EnrichedPDP.prototype._recompute = function () {
    var gsig = this._garments().map(function (g) { return g.key; }).join(',');
    if (gsig !== this._gsig) {
      this._gsig = gsig; var selfR = this;
      var rl = this._orderedSelected().filter(function (x) { return x.source === 'text' && x.perName && selfR.v3; });
      if (rl.length && !(document.activeElement && document.activeElement.closest && document.activeElement.closest('.bmn-epdp__roster'))) { rl.forEach(function (x) { selfR._syncRoster(x.id); }); this._renderLines(); this._renderOverlay(this._orderedSelected()); }
    }
    var total = this._totalUnits();
    var lines = this._orderedSelected();
    var apps = total * lines.length;
    var perShirtDecoration = 0;
    var self = this;
    lines.forEach(function (l) { perShirtDecoration += self._rateForLine(l, apps); });
    var expressOn = this._expressOn(lines);
    var perShirt = this.blank + perShirtDecoration + (expressOn ? this.expressFee : 0);
    var subtotal = perShirt * total;
    var expressWrap = this.root.querySelector('.bmn-epdp__express-wrap');
    if (expressWrap) expressWrap.hidden = !(this.expressFee && lines.length);
    this.root.querySelectorAll('.bmn-epdp__express-fee').forEach(function (el) { el.textContent = self._plain(self.expressFee); });
    // Setup (digitising) applies to embroidery only. DTF print never carries a setup charge.
    // Names and text are stitched from machine fonts, so only an embroidered logo needs setup.
    var hasEmb = lines.some(function (l) { return l.method === 'embroidery' && l.source !== 'text'; });
    var setupInfo = this._setupInfo(lines, subtotal);
    var setup = (hasEmb && !this.repeat) ? this.setupFee * setupInfo.count : 0;
    var orderTotal = subtotal + setup;
    var repeatWrap = this.root.querySelector('.bmn-epdp__repeat-wrap');
    if (repeatWrap) repeatWrap.hidden = !hasEmb;

    // refresh per-line uplifts (band changes with total applications)
    this.root.querySelectorAll('.bmn-epdp__line').forEach(function (lineEl) {
      var id = lineEl.getAttribute('data-pos'); var s = self.selected[id]; if (!s) return;
      var ln = lines.filter(function (x) { return x.id === id; })[0];
      var up = self._rateForLine(ln || { method: s.method, oversized: !!s.oversized, highStitch: !!s.highStitch }, apps);
      var u = lineEl.querySelector('.bmn-epdp__line-uplift');
      if (u) u.innerHTML = '+ ' + self._dual(up) + ' <small>each</small>';
    });

    this.root.querySelectorAll('.bmn-epdp__names').forEach(function (ta) { var c = ta.parentNode.querySelector('.bmn-epdp__names-count'); if (c) c.textContent = namesCountText(splitNames(ta.value).length, total); });
    this.elTotalUnits.forEach(function (el) { el.textContent = total; });
    this.root.querySelectorAll('.bmn-epdp__units-word').forEach(function (el) { el.textContent = total === 1 ? 'unit' : 'units'; });
    var laterNote = this.root.querySelector('.bmn-epdp__later-note');
    if (laterNote) laterNote.hidden = !lines.some(function (l) { return l.source === 'later' || l.source === 'redraw'; });
    if (this.elPerShirt) this.elPerShirt.innerHTML = this._dual(perShirt);
    this.elOrderTotal.forEach(function (el) { el.innerHTML = self._dual(orderTotal); });
    if (this.elTier) {
      this.elTier.textContent = (total > 0 && lines.length)
        ? (apps + ' application' + (apps !== 1 ? 's' : '') + ' (' + total + ' unit' + (total !== 1 ? 's' : '') + ' × ' + lines.length + ' position' + (lines.length !== 1 ? 's' : '') + ')')
        : '';
    }
    if (this.elSetup) {
      if (!lines.length) this.elSetup.textContent = '';
      else if (!hasEmb) this.elSetup.textContent = lines.some(function (l) { return l.method === 'embroidery'; }) ? 'No setup charge for names and text.' : 'No setup charge for printed logos.';
      else if (this.repeat) this.elSetup.textContent = 'No logo setup (repeat order).';
      else this.elSetup.textContent = this._setupNote(setupInfo);
    }
    this._renderBreaks(lines, total);
    this._renderRecommendation(lines);
    // One page layout: the bar follows the visitor whenever the main button is off screen.
    var onePage = this.root.classList.contains('bmn-epdp--v2');
    if (this.sticky) this.sticky.classList.toggle('is-ready', total > 0 && (onePage ? !this._addInView : lines.length > 0));
    this.addBtns.forEach(function (b) { b.disabled = total === 0; });
    if (this.v3) this._v3Render(total, lines);
  };

  EnrichedPDP.prototype._renderBreaks = function (lines, total) {
    if (!this.elBreak) return;
    if (!lines.length) { this.elBreak.innerHTML = ''; return; }
    var self = this;
    var mil = [10, 25, 50, 100, 250, 500];
    var rows = mil.map(function (units, i) {
      var apps = units * lines.length;
      var dec = 0; lines.forEach(function (l) { dec += self._rateForLine(l, apps); });
      var ps = self.blank + dec;
      var next = mil[i + 1];
      var active = total >= units && (next == null || total < next);
      return '<tr' + (active ? ' class="is-active"' : '') + '><td>' + units + '+ units</td><td>' + self._dual(ps) + ' <small>each</small></td></tr>';
    }).join('');
    this.elBreak.innerHTML = '<table class="bmn-epdp__break-table"><thead><tr><th>Order size</th><th>Per item, decorated (<span class="vat-inc">inc VAT</span><span class="vat-ex">ex VAT</span>)</th></tr></thead><tbody>' + rows + '</tbody></table>';
  };

  EnrichedPDP.prototype._renderRecommendation = function (lines) {
    if (!this.elRec) return;
    var hasChest = lines.some(function (l) { return /chest|nape/.test(l.id) || ['lc','rc','cc','ff','np'].indexOf(l.id) !== -1; });
    var hasBack = lines.some(function (l) { return ['ub','fb'].indexOf(l.id) !== -1; });
    var tips = [];
    if (this.printOnly) {
      tips.push('This garment is waterproof, so we print your logo. Embroidery needles pierce the waterproof layer and can let water in.');
      this.elRec.innerHTML = tips.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('');
      return;
    }
    if (this.vis && (this.vis.tips || HEADWEAR.test(this.vis.tpl))) {
      tips = (this.vis.tips || []).slice();
      this.elRec.innerHTML = tips.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('');
      return;
    }
    if (hasChest) tips.push('Chest and nape logos look sharpest embroidered.');
    if (hasBack) tips.push('Large back artwork is best as a DTF print (full colour, no stitch limit).');
    if (!lines.length) tips.push('Most workwear uses a left-chest logo. Add positions above to price it up.');
    tips.push('Lightweight 100% polyester (160gsm or under) prints better than it embroiders, we will flag this on your proof.');
    this.elRec.innerHTML = tips.map(function (t) { return '<li>' + esc(t) + '</li>'; }).join('');
  };

  // ---------- builder v3 (pilot) ----------
  // Small garment drawings for the position buttons. Dot = where the logo goes.
  var POS_ICON = {
    lc: '<circle class="d" cx="14.6" cy="10" r="1.6"/>',
    rc: '<circle class="d" cx="9.4" cy="10" r="1.6"/>',
    cc: '<rect class="d" x="9.5" y="9" width="5" height="3.4" rx=".6"/>',
    ff: '<rect class="d" x="8.4" y="7.6" width="7.2" height="9.4" rx=".8"/>',
    np: '<rect class="d" x="10.6" y="5.2" width="2.8" height="1.6" rx=".4"/>',
    ub: '<rect class="d" x="9" y="7.4" width="6" height="2.6" rx=".5"/>',
    fb: '<rect class="d" x="8.4" y="8" width="7.2" height="8.6" rx=".8"/>',
    ls: '<circle class="d" cx="20" cy="7.6" r="1.4"/>',
    rs: '<circle class="d" cx="4" cy="7.6" r="1.4"/>'
  };
  var SHIRT = '<path class="g" d="M8.2 3.2 3 6.1l1.9 3.7 2-1V21h10.2V8.8l2 1L21 6.1l-5.2-2.9c-.6 1.4-2 2.3-3.8 2.3S8.8 4.6 8.2 3.2z"/>';
  // Headwear (beanies, caps): front only, hat icon, no back view.
  var HEADWEAR = /^(beanie|cap)$/;
  var HAT = '<path class="g" d="M4.5 14.5C4.5 8.6 7.9 4 12 4s7.5 4.6 7.5 10.5z"/><rect class="g" x="3.5" y="13.5" width="17" height="6" rx="1.2"/>';
  var HAT_DOT = '<rect class="d" x="8.6" y="15" width="6.8" height="3" rx=".6"/>';
  // Where a front logo sits on the main photo (share of the image box), for the rough preview.
  var OVERLAY_SPOT = { lc: { x: 61, y: 31, w: 12 }, rc: { x: 39, y: 31, w: 12 }, cc: { x: 50, y: 35, w: 22 }, ff: { x: 50, y: 45, w: 30 } };

  EnrichedPDP.prototype._v3Init = function () {
    var self = this, r = this.root;
    // Visualiser v1: hide positions this garment does not have, and show the proof disclaimer.
    var visApplied = false;
    function applyVis() {
      var v = self.vis; if (!v || visApplied) return; visApplied = true;
      if (v.positions.length) {
        var frontIds = ['lc', 'rc', 'cc', 'ff', 'ls', 'rs'], noBack = /^(apron|beanie|cap)$/.test(v.tpl);
        self.posButtons.forEach(function (b) {
          var id = b.getAttribute('data-id');
          var ok = frontIds.indexOf(id) < 0 ? !noBack : v.positions.indexOf(id) >= 0;
          if (!ok) { b.style.display = 'none'; b.disabled = true; if (self.selected[id]) self._togglePosition(id); }
        });
      }
      if (v.sizes) SIZE_OVR = v.sizes;
      if (HEADWEAR.test(v.tpl)) {
        self.posButtons.forEach(function (b) {
          var id = b.getAttribute('data-id'), lab = v.labels && v.labels[id];
          if (lab) { Array.prototype.slice.call(b.childNodes).forEach(function (n) { if (n.nodeType === 3) n.remove(); }); b.appendChild(document.createTextNode(lab)); }
          var ico = b.querySelector('.bmn-epdp__pos-ico'); if (ico) ico.innerHTML = HAT + HAT_DOT;
        });
        var vsw = r.querySelector('.bmn-epdp__views'); if (vsw) { vsw.hidden = true; vsw.style.display = 'none'; }
        if (self.view === 'back') self._setView('front');
      } else if (v.labels) {
        self.posButtons.forEach(function (b) {
          var lab = v.labels[b.getAttribute('data-id')]; if (!lab) return;
          Array.prototype.slice.call(b.childNodes).forEach(function (n) { if (n.nodeType === 3) n.remove(); }); b.appendChild(document.createTextNode(lab));
        });
      }
      var note = r.querySelector('.bmn-epdp__overlay-note');
      if (note) note.textContent = 'Preview only. Nothing is produced until you approve your free proof.';
      var oldBack = r.querySelector('.bmn-epdp__backview .bmn-epdp__back-svg');
      if (oldBack) oldBack.outerHTML = backSvg(v.tpl);
      var changed = false;
      Object.keys(self.selected).forEach(function (id) {
        if (!/^(lc|rc|cc|ff|ls|rs)$/.test(id) || self.printOnly) return;
        var want = v.embOnly ? 'embroidery' : (v.def === 'dtf' ? 'dtf' : null);
        if (want && self.selected[id].method !== want) { self.selected[id].method = want; changed = true; }
      });
      if (changed || v.embOnly) { self._renderLines(); self._recompute(); }
      else if (v.sizes || v.labels || v.tips || HEADWEAR.test(v.tpl)) setTimeout(function () { self._renderLines(); self._recompute(); }, 0);
      self._frontHtml = self._backHtml = null; self._renderOverlay(self._orderedSelected());
    }
    applyVis();
    if (!visApplied) { document.addEventListener('DOMContentLoaded', applyVis); window.addEventListener('load', applyVis); }
    this.posButtons.forEach(function (b) {
      var id = b.getAttribute('data-id');
      if (POS_ICON[id] && !b.querySelector('.bmn-epdp__pos-ico')) {
        var hat = self.vis && HEADWEAR.test(self.vis.tpl);
        b.insertAdjacentHTML('afterbegin', '<svg class="bmn-epdp__pos-ico" viewBox="0 0 24 24" aria-hidden="true">' + (hat ? HAT + HAT_DOT : SHIRT + POS_ICON[id]) + '</svg>');
      }
    });
    this.ladderEl = r.querySelector('.bmn-epdp__ladder');
    this.mixEl = r.querySelector('.bmn-epdp__mix');
    this.oosEl = r.querySelector('.bmn-epdp__oos');
    this.etaEl = r.querySelector('.bmn-epdp__eta');
    this.overlay = r.querySelector('.bmn-epdp__overlay');
    this.overlayNote = r.querySelector('.bmn-epdp__overlay-note');
    this.galleryImg = r.querySelector('.bmn-epdp__gallery-img');
    this.ladderTab = this.printOnly ? 'dtf' : 'emb';
    if (this.ladderEl) this.ladderEl.addEventListener('click', function (e) {
      var t = e.target.closest('.bmn-epdp__ladder-tab'); if (!t) return;
      self.ladderTab = t.getAttribute('data-tab'); self._renderLadder(self._totalUnits());
    });
    if (this.mixEl) this.mixEl.addEventListener('click', function (e) {
      var c = e.target.closest('.bmn-epdp__mix-chip'); if (!c) return;
      var sw = self._swatchFor(c.getAttribute('data-colour')); if (sw) self._selectColour(sw);
    });
    if (this.oosEl) this.oosEl.addEventListener('click', function (e) {
      var c = e.target.closest('[data-colour]'); if (!c) return;
      var sw = self._swatchFor(c.getAttribute('data-colour')); if (sw) self._selectColour(sw);
    });
    Array.prototype.forEach.call(r.querySelectorAll('.bmn-epdp__go-logo'), function (b) {
      b.addEventListener('click', function () { self._goLogo(); });
    });
    if (this.galleryImg) this.galleryImg.addEventListener('load', function () { self._renderOverlay(self._orderedSelected()); });
    this._loadRelated();
    // Re-fit text previews once the web fonts have arrived
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self._frontHtml = self._backHtml = null; self._renderOverlay(self._orderedSelected()); });
  };

  EnrichedPDP.prototype._swatchFor = function (colour) {
    return this.swatches.filter(function (s) { return s.getAttribute('data-value') === colour; })[0] || null;
  };

  // "Add your logo": most workwear carries a left chest logo, so start there and take the visitor to step 3.
  EnrichedPDP.prototype._goLogo = function () {
    var first = this.printOnly ? 'cc' : 'lc';
    if (this.vis && this.vis.positions.length && this.vis.positions.indexOf(first) < 0) first = this.vis.positions[0];
    if (!this._orderedSelected().length) this._togglePosition(first);
    var box = this.root.querySelector('.bmn-epdp__positions');
    if (box) {
      box.scrollIntoView({ behavior: 'smooth', block: 'center' });
      box.classList.remove('is-flash'); void box.offsetWidth; box.classList.add('is-flash');
    }
  };

  EnrichedPDP.prototype._v3Render = function (total, lines) {
    var r = this.root;
    r.classList.toggle('is-branded', lines.length > 0);
    var addText = lines.length ? 'Add to cart' : 'Or buy it plain, no logo';
    this.addBtns.forEach(function (b) { if (!b.classList.contains('is-loading')) b.textContent = addText; });
    var blocks = r.querySelectorAll('.bmn-epdp__main > .bmn-epdp__block');
    if (blocks[0]) blocks[0].classList.toggle('is-done', !!this.colour && this.swatches.length > 0);
    if (blocks[1]) blocks[1].classList.toggle('is-done', total > 0);
    if (blocks[2]) blocks[2].classList.toggle('is-done', lines.length > 0);
    this._renderLadder(total);
    this._renderMix();
    this._renderOos();
    this._renderEta(lines);
    this._renderOverlay(lines);
  };

  EnrichedPDP.prototype._renderLadder = function (total) {
    if (!this.ladderEl) return;
    var self = this, r = this.ratesP, tab = this.ladderTab || 'emb';
    var tabs = [];
    if (!this.printOnly) tabs.push(['emb', 'Embroidered']);
    var embOnlyVis = this.vis && this.vis.embOnly && !this.printOnly;
    if (embOnlyVis && tab === 'dtf') tab = 'emb';
    if (!embOnlyVis) tabs.push(['dtf', 'Printed']);
    tabs.push(['plain', 'Plain']);
    var rows = [], note = '';
    if (tab === 'plain') {
      rows = [{ label: 'Any quantity', price: this.blank }];
      note = 'Plain garment, no logo. Add a logo for the prices on the other tabs.';
    } else {
      var bands = tab === 'emb' ? [1, 50, 250, 500] : [1, 9, 25, 100, 250, 500];
      rows = bands.map(function (u) {
        var rate = tab === 'emb' ? r.embStd[embBand(u)] : r.dtf[dtfBand(u)];
        return { label: u + '+', from: u, price: self.blank + (rate || 0) };
      });
      note = tab === 'emb'
        ? 'Per item with one standard embroidered logo. ' + (this.setupFee ? 'Logo setup ' + this._plain(this.setupFee) + ' per design' + (this.setupFreeOver || this.setupFreeUnits ? ', first design free on ' + this._setupRuleText() : '') + ', free on repeat orders. ' : '') + 'Your exact price shows in the total below.'
        : 'Per item with one printed logo. No setup charge. Your exact price shows in the total below.';
    }
    var first = rows[0] ? rows[0].price : 0;
    var cells = rows.map(function (row, i) {
      var next = rows[i + 1];
      var active = tab === 'plain' ? total > 0 : (total >= row.from && (!next || total < next.from));
      var save = (first && row.price < first) ? Math.round((1 - row.price / first) * 100) : 0;
      return '<div class="bmn-epdp__ladder-cell' + (active ? ' is-active' : '') + '"><b>' + esc(row.label) + '</b><strong>' + self._dual(row.price) + '</strong><em>' + (save ? 'Save ' + save + '%' : '') + '</em></div>';
    }).join('');
    var ladderHtml =
      '<div class="bmn-epdp__ladder-tabs" role="tablist">' + tabs.map(function (t) {
        return '<button type="button" role="tab" class="bmn-epdp__ladder-tab' + (t[0] === tab ? ' is-on' : '') + '" data-tab="' + t[0] + '" aria-selected="' + (t[0] === tab) + '">' + t[1] + '</button>';
      }).join('') + '</div>' +
      '<div class="bmn-epdp__ladder-rows" data-n="' + rows.length + '">' + cells + '</div>' +
      '<p class="bmn-epdp__ladder-note">' + esc(note) + ' Prices <span class="vat-inc">inc VAT</span><span class="vat-ex">ex VAT</span>, each.</p>';
    if (ladderHtml !== this._ladderHtml) { this.ladderEl.innerHTML = ladderHtml; this._ladderHtml = ladderHtml; }
  };

  EnrichedPDP.prototype._renderMix = function () {
    if (!this.mixEl) return;
    var orders = this._colourOrders(), self = this;
    if (!orders.length) { this.mixEl.hidden = true; this.mixEl.innerHTML = ''; this._mixHtml = ''; return; }
    var chips = orders.map(function (o) {
      var sw = self._swatchFor(o.colour), th = sw ? sw.getAttribute('data-thumb') : '';
      var bg = th ? ' style="background-image:url(' + esc(JSON.stringify(th)) + ')"' : (sw && sw.style.background ? ' style="background:' + esc(sw.style.background) + '"' : '');
      return '<button type="button" class="bmn-epdp__mix-chip' + (o.colour === self.colour ? ' is-current' : '') + '" data-colour="' + esc(o.colour) + '" title="' + esc(o.sizes) + '"><span class="bmn-epdp__mix-dot"' + bg + '></span>' + esc(displayName(o.colour)) + ' <span>' + o.units + '</span></button>';
    }).join('');
    var hint = orders.length > 1
      ? 'Tap a colour to change its sizes. Every colour counts towards your price band.'
      : 'Need another colour too? Pick it above. Your ' + esc(displayName(this.colour)) + ' sizes are kept and every colour counts towards your price band.';
    var mixHtml = '<span class="bmn-epdp__mix-label">Your order:</span>' + chips + '<span class="bmn-epdp__mix-hint">' + hint + '</span>';
    // Only redraw when something changed, so a tap on a chip is never lost to a redraw.
    if (mixHtml !== this._mixHtml) { this.mixEl.innerHTML = mixHtml; this._mixHtml = mixHtml; }
    this.mixEl.hidden = false;
  };

  EnrichedPDP.prototype._renderOos = function () {
    if (!this.oosEl) return;
    var self = this, si = this.sizeIndex, ci = this.colourIndex;
    var gone = this.sizeRows.filter(function (row) { return row.classList.contains('is-unavailable') && row.getAttribute('data-size') !== 'Default Title'; })
      .map(function (row) { return row.getAttribute('data-size'); });
    if (!gone.length || this.loadingColour === this.colour) { this.oosEl.hidden = true; this.oosEl.innerHTML = ''; this._oosHtml = ''; return; }
    // Other colours of this garment that have every missing size available
    var others = [];
    if (ci >= 0) {
      this.swatches.forEach(function (sw) {
        var c = sw.getAttribute('data-value'); if (c === self.colour) return;
        var ok = gone.every(function (sz) {
          return self.variants.some(function (v) { return v.options[ci] === c && v.options[si] === sz && v.available; });
        });
        if (ok) others.push(c);
      });
    }
    var html = '<strong>' + esc(gone.join(', ')) + '</strong> ' + (gone.length === 1 ? 'is' : 'are') + ' not available in ' + esc(displayName(this.colour)) + ' right now.';
    if (others.length) html += '<div class="bmn-epdp__oos-colours"><span>Try another colour:</span>' + others.slice(0, 8).map(function (c) { return '<button type="button" data-colour="' + esc(c) + '">' + esc(displayName(c)) + '</button>'; }).join('') + '</div>';
    if (this.related && this.related.length) {
      html += '<div class="bmn-epdp__oos-styles"><span>Or a similar style:</span>' + this.related.map(function (p) {
        return '<a href="' + esc(p.url) + '">' + (p.img ? '<img src="' + esc(p.img) + '" alt="" width="36" height="36" loading="lazy">' : '') + esc(p.title) + '</a>';
      }).join('') + '</div>';
    }
    if (html !== this._oosHtml) { this.oosEl.innerHTML = html; this._oosHtml = html; }
    this.oosEl.hidden = false;
  };

  EnrichedPDP.prototype._loadRelated = function () {
    var id = this.root.getAttribute('data-product-id'), self = this;
    if (!id || !window.fetch) return;
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(base + 'recommendations/products.json?product_id=' + encodeURIComponent(id) + '&limit=3&intent=related')
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (d) {
        self.related = ((d && d.products) || []).filter(function (p) { return p.available; }).slice(0, 3).map(function (p) {
          var img = p.featured_image || '';
          if (img) img = img.replace(/(\.[a-z]+)(\?|$)/i, '$1$2') + (img.indexOf('?') === -1 ? '?width=96' : '&width=96');
          return { title: p.title, url: p.url, img: img };
        });
        self._renderOos();
      }).catch(function () {});
  };

  // Delivery estimate: working days from today, weekends skipped. Bank holidays not counted, so it says "about".
  function addWorkingDays(d, n) {
    var x = new Date(d.getTime()), added = 0;
    while (added < n) { x.setDate(x.getDate() + 1); var w = x.getDay(); if (w !== 0 && w !== 6) added++; }
    return x;
  }
  function shortDate(d) {
    var days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], mons = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return days[d.getDay()] + ' ' + d.getDate() + ' ' + mons[d.getMonth()];
  }
  EnrichedPDP.prototype._renderEta = function (lines) {
    if (!this.etaEl) return;
    var lo = parseInt(this.root.getAttribute('data-eta-min'), 10) || 0, hi = parseInt(this.root.getAttribute('data-eta-max'), 10) || 0;
    if (!lo || !hi) { this.etaEl.hidden = true; return; }
    if (this._expressOn(lines)) {
      var d = shortDate(addWorkingDays(new Date(), this.expressDays));
      this.etaEl.innerHTML = '<span>Express: approve your mockup today and your branded kit is ready to ship by about <strong>' + d + '</strong>' +
        '<small>' + this.expressDays + ' working days from artwork approval.</small></span>';
      this.etaEl.hidden = false; return;
    }
    var now = new Date();
    var a = shortDate(addWorkingDays(now, lo)), b = shortDate(addWorkingDays(now, hi));
    this.etaEl.innerHTML = '<span>Approve your mockup today and your branded kit is ready to ship by about <strong>' + a + ' to ' + b + '</strong>' +
      '<small>' + lo + ' to ' + hi + ' working days from artwork approval. Got a deadline? Tell us and we will do our best.</small></span>';
    this.etaEl.hidden = false;
  };

  // ---------- builder v3: preview of every position, front photo and back view ----------
  var FONTS = {
    block:  { label: 'Block',   css: "800 1em 'Barlow Condensed', 'Arial Narrow', sans-serif", upper: true },
    sans:   { label: 'Modern',  css: "700 1em Arial, Helvetica, sans-serif" },
    serif:  { label: 'Classic', css: "700 1em Georgia, 'Times New Roman', serif" },
    script: { label: 'Script',  css: "700 1em 'Dancing Script', 'Brush Script MT', cursive" },
    college:{ label: 'College', css: "400 1em Graduate, 'Rockwell', serif", upper: true }
  };
  var INKS = [
    ['white', 'White', '#ffffff'], ['black', 'Black', '#111111'], ['navy', 'Navy', '#1b2440'], ['royal', 'Royal blue', '#1f4fb0'],
    ['red', 'Red', '#c0202a'], ['gold', 'Gold', '#c9a227'], ['yellow', 'Yellow', '#f4d03f'], ['silver', 'Silver grey', '#bfc3c7'],
    ['green', 'Bottle green', '#0f5b3e'], ['orange', 'Orange', '#e8732a'], ['pink', 'Pink', '#e58fb0'], ['purple', 'Purple', '#5b3a8a']
  ];
  function inkHex(k) { for (var i = 0; i < INKS.length; i++) if (INKS[i][0] === k) return INKS[i][2]; return '#ffffff'; }
  function inkLabel(k) { for (var i = 0; i < INKS.length; i++) if (INKS[i][0] === k) return INKS[i][1]; return 'White'; }
  function fontLabel(k) { return (FONTS[k] || FONTS.block).label; }
  // Where each position sits, as a share of the picture box. Front = the colour photo, back = the drawn back view.
  var SPOTS = {
    lc: { v: 'front', x: 61, y: 31, w: 12 }, rc: { v: 'front', x: 39, y: 31, w: 12 }, cc: { v: 'front', x: 50, y: 35, w: 22 }, ff: { v: 'front', x: 50, y: 45, w: 30 },
    ls: { v: 'front', x: 83, y: 33, w: 7 },  rs: { v: 'front', x: 17, y: 33, w: 7 },
    np: { v: 'back',  x: 50, y: 15, w: 7 },  ub: { v: 'back',  x: 50, y: 25, w: 27 }, fb: { v: 'back',  x: 50, y: 46, w: 30 }
  };
  // Logo width in mm per position: [smallest, largest embroidered, largest printed, starting size].
  // Follows Clothes2Order and Printful (Anil, 13 Oct 2026): chest 65 to 120 embroidered (C2O 80 to 120,
  // Printful from 2.5in), 125 printed (Printful 5in); sleeve 80 to 100; nape label 75 (Printful 3in);
  // top back 150 to 250 embroidered; embroidery max 250 (C2O), print max 300 (Printful 12in).
  var SIZES = {
    lc: [65, 120, 125, 100], rc: [65, 120, 125, 100], cc: [100, 250, 300, 200], ff: [150, 250, 300, 250],
    ls: [80, 100, 100, 90], rs: [80, 100, 100, 90], np: [50, 75, 75, 70],
    ub: [150, 250, 300, 200], fb: [200, 250, 300, 250]
  };
  var SIZE_OVR = null;   // per-product logo sizes from the visualiser (e.g. the 10 x 5 cm beanie patch)
  var AREA_MM = null;   // widest logo each hand-set print area takes, in mm
  function areaMM(a) {
    if (!a) return null; var o = {};
    ['front', 'back'].forEach(function (v) { var s = a[v] || {}; Object.keys(s).forEach(function (k) { if (s[k] && s[k].mm && !s[k].off) o[k] = s[k].mm; }); });
    if (a.areas) Object.keys(a.areas).forEach(function (k) { if (a.areas[k] && a.areas[k].w) o[k] = Math.max(o[k] || 0, +a.areas[k].w); });
    return Object.keys(o).length ? o : null;
  }
  function sizeRange(l) {
    var r = (SIZE_OVR && SIZE_OVR[l.id]) || SIZES[l.id] || [50, 250, 300, 90];
    var hi = l.method === 'dtf' ? r[2] : r[1];
    if (AREA_MM && AREA_MM[l.id]) hi = Math.max(r[0], Math.min(hi, AREA_MM[l.id]));
    return [r[0], hi, Math.min(r[3], hi)];
  }
  // About how many mm one percent of the picture box covers (front: the polo photo, back: the drawn back view).
  var MM_PER_PCT = { front: 6.3, back: 9.3, side: 8.5 };
  function sizeOf(l) { var r = sizeRange(l); var v = parseInt(l.mm, 10) || r[2]; return Math.max(r[0], Math.min(r[1], v)); }
  var BACK_SVG = '<svg class="bmn-epdp__back-svg" viewBox="0 0 100 100" aria-hidden="true">' +
    '<defs><linearGradient id="bmnShade" x1="0" x2="1"><stop offset="0" stop-color="#000" stop-opacity=".16"/><stop offset=".22" stop-color="#000" stop-opacity="0"/><stop offset=".78" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".16"/></linearGradient></defs>' +
    '<path class="bmn-epdp__back-body" d="M36 7.5c4 2.6 24 2.6 28 0l17.5 6.5 15 20.5-12.5 8.2-6-5.4V95H22V37.3l-6 5.4L3.5 34.5l15-20.5z"/>' +
    '<path d="M36 7.5c4 2.6 24 2.6 28 0l17.5 6.5 15 20.5-12.5 8.2-6-5.4V95H22V37.3l-6 5.4L3.5 34.5l15-20.5z" fill="url(#bmnShade)"/>' +
    '<path class="bmn-epdp__back-collar" d="M36 7.5c4 2.6 24 2.6 28 0l-1.2 4.2c-4.6 2.2-21 2.2-25.6 0z"/>' +
    '<path d="M22 37.3V95h56V37.3" fill="none" stroke="rgba(0,0,0,.18)" stroke-width=".5"/></svg>';

  // Back view outline per garment family (Visualiser v1): short sleeve, long sleeve, jacket collar, hood, sleeveless.
  var BACK_BODY = {
    short: 'M36 7.5c4 2.6 24 2.6 28 0l17.5 6.5 15 20.5-12.5 8.2-6-5.4V95H22V37.3l-6 5.4L3.5 34.5l15-20.5z',
    long: 'M36 7.5c4 2.6 24 2.6 28 0L81.5 14l13 64-9.5 2.5L78 37.3V95H22V37.3l-7 43.2L5.5 78l13-64z',
    vest: 'M36 7.5c4 2.6 24 2.6 28 0l12 3c.5 9 2 18 5 25V95H19V35.5c3-7 4.5-16 5-25z'
  };
  var BACK_TOP = {
    collar: 'M36 7.5c4 2.6 24 2.6 28 0l-1.2 4.2c-4.6 2.2-21 2.2-25.6 0z',
    stand: 'M35 8c4 2.6 26 2.6 30 0l-.6-5.5c-4.2 1.8-24.6 1.8-28.8 0z',
    hood: 'M33.5 9.5C31-1 69-1 66.5 9.5l-2.5 7c-4.5 2.4-23.5 2.4-28 0z'
  };
  function backSvg(tpl) {
    var fam = /^(hoodie)$/.test(tpl) ? ['long', 'hood'] : /^(softshell|fleece)$/.test(tpl) ? ['long', 'stand'] : /^(sweatshirt|shirt)$/.test(tpl) ? ['long', 'collar']
      : /^(gilet)$/.test(tpl) ? ['vest', 'stand'] : /^(hivis-vest)$/.test(tpl) ? ['vest', 'collar'] : null;
    if (!fam) return BACK_SVG;
    var b = BACK_BODY[fam[0]], t = BACK_TOP[fam[1]];
    return '<svg class="bmn-epdp__back-svg" viewBox="0 0 100 100" aria-hidden="true">' +
      '<defs><linearGradient id="bmnShade" x1="0" x2="1"><stop offset="0" stop-color="#000" stop-opacity=".16"/><stop offset=".22" stop-color="#000" stop-opacity="0"/><stop offset=".78" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".16"/></linearGradient></defs>' +
      '<path class="bmn-epdp__back-body" d="' + b + '"/><path d="' + b + '" fill="url(#bmnShade)"/>' +
      '<path class="bmn-epdp__back-collar" d="' + t + '"/><path d="' + t + '" fill="rgba(0,0,0,.08)"/>' +
      (fam[0] === 'vest' ? '' : '<path d="M22 37.3V95h56V37.3" fill="none" stroke="rgba(0,0,0,.18)" stroke-width=".5"/>') + '</svg>';
  }

  EnrichedPDP.prototype._garmentColour = function () {
    var sw = this._swatchFor(this.colour), self = this;
    var src = sw ? (sw.getAttribute('data-thumb') || sw.getAttribute('data-image')) : null;
    if (!src) { var n = (this.colour || '').toLowerCase().replace(/\*/g, '').trim(); return COLOUR_MAP[n] || '#9b9b9b'; }
    this._inkCache = this._inkCache || {};
    if (this._inkCache[src]) return this._inkCache[src];
    if (!this._sampling) {
      this._sampling = src;
      var im = new Image(); im.crossOrigin = 'anonymous';
      im.onload = function () {
        try {
          var c = document.createElement('canvas'); c.width = 40; c.height = 40;
          var x = c.getContext('2d'); x.drawImage(im, 0, 0, 40, 40);
          var d = x.getImageData(17, 20, 6, 6).data, r = 0, g = 0, b = 0, n2 = 0;
          for (var i = 0; i < d.length; i += 4) { r += d[i]; g += d[i + 1]; b += d[i + 2]; n2++; }
          self._inkCache[src] = 'rgb(' + Math.round(r / n2) + ',' + Math.round(g / n2) + ',' + Math.round(b / n2) + ')';
        } catch (e) { self._inkCache[src] = sw.style.background || '#9b9b9b'; }
        self._sampling = null; self._renderOverlay(self._orderedSelected());
      };
      im.onerror = function () { self._inkCache[src] = '#9b9b9b'; self._sampling = null; };
      im.src = src;
    }
    var n3 = (this.colour || '').toLowerCase().replace(/\*/g, '').trim();
    return COLOUR_MAP[n3] || '#9b9b9b';
  };
  function isLight(css) {
    var m = String(css).match(/rgb\((\d+),(\d+),(\d+)\)/), r, g, b;
    if (m) { r = +m[1]; g = +m[2]; b = +m[3]; }
    else if (/^#[0-9a-f]{6}$/i.test(css)) { r = parseInt(css.substr(1, 2), 16); g = parseInt(css.substr(3, 2), 16); b = parseInt(css.substr(5, 2), 16); }
    else return false;
    return (0.299 * r + 0.587 * g + 0.114 * b) > 165;
  }
  // Thread or print colour that stands out on the chosen garment: black on light colours, white otherwise.
  EnrichedPDP.prototype._defaultInk = function () { return isLight(this._garmentColour()) ? 'black' : 'white'; };

  EnrichedPDP.prototype._sizeControl = function (l) {
    var r = sizeRange(l), v = sizeOf(l);
    return '<label class="bmn-epdp__sizectl"><span>' + (l.source === 'text' ? 'Text width' : 'Logo width') + ': <b class="bmn-epdp__size-val">' + v + 'mm</b> <small class="bmn-epdp__size-in">(about ' + (v / 25.4).toFixed(1) + ' in)</small></span>' +
      '<input type="range" class="bmn-epdp__size" data-pos="' + l.id + '" min="' + r[0] + '" max="' + r[1] + '" step="5" value="' + v + '" aria-label="Width in millimetres for ' + esc(l.label) + '">' +
      '<span class="bmn-epdp__size-range">' + r[0] + 'mm</span><span class="bmn-epdp__size-range">' + r[1] + 'mm max ' + (l.method === 'dtf' ? 'printed' : 'embroidered') + '</span></label>';
  };

  EnrichedPDP.prototype._textControls = function (l) {
    var word = l.method === 'dtf' ? 'Print colour' : 'Thread colour';
    var fonts = Object.keys(FONTS).map(function (k) { return '<option value="' + k + '"' + (l.font === k ? ' selected' : '') + '>' + FONTS[k].label + '</option>'; }).join('');
    var inks = INKS.map(function (c) {
      return '<button type="button" class="bmn-epdp__tcol' + (l.tcolour === c[0] ? ' is-on' : '') + '" data-pos="' + l.id + '" data-colour="' + c[0] + '" style="background:' + c[2] + '" title="' + c[1] + '" aria-label="' + word + ' ' + c[1] + '" aria-pressed="' + (l.tcolour === c[0]) + '"></button>';
    }).join('');
    return '<div class="bmn-epdp__textctl">' +
      '<label class="bmn-epdp__textctl-row"><span>Font</span><select class="bmn-epdp__font" data-pos="' + l.id + '" aria-label="Font for ' + esc(l.label) + '">' + fonts + '</select></label>' +
      '<div class="bmn-epdp__textctl-row"><span>' + word + ': <b>' + esc(inkLabel(l.tcolour)) + '</b></span><div class="bmn-epdp__tcols">' + inks + '</div></div>' +
    '</div>';
  };

  // Show the side of the garment where this position sits.
  EnrichedPDP.prototype._viewFor = function (id) {
    var sp = SPOTS[id]; if (!sp) return;
    if ((id === 'ls' || id === 'rs') && this.sideView) { var sl = this._orderedSelected().filter(function (x) { return x.id === id; })[0], svs = sl && this._visSpot(sl); if (sl && !(svs && svs.scene)) { this._setView('side'); return; } }
    this._setView(sp.v);
  };
  EnrichedPDP.prototype._setView = function (v) {
    this.view = v === 'back' ? 'back' : v === 'side' ? 'side' : 'front';
    var bv = this.root.querySelector('.bmn-epdp__backview');
    if (bv) bv.hidden = this.view !== 'back';
    if (this.sideView) this.sideView.hidden = this.view !== 'side';
    Array.prototype.forEach.call(this.root.querySelectorAll('.bmn-epdp__view-btn'), function (b) {
      var on = b.getAttribute('data-view') === this.view; b.classList.toggle('is-on', on); b.setAttribute('aria-pressed', on ? 'true' : 'false');
    }, this);
    this._renderOverlay(this._orderedSelected());
  };

  // Visualiser v1: where a front position sits on this garment's own photo, from its print-area template.
  EnrichedPDP.prototype._visSpot = function (l) {
    var V = window.BMNVis, self = this; if (!V || !this.vis || !this.galleryImg) return null;
    var src = this.galleryImg.currentSrc || this.galleryImg.src; if (!src) return null;
    var g = V.measured(src);
    if (g === undefined) {
      if (this._measuring !== src) { this._measuring = src; V.measure(src).then(function () { self._measuring = null; self._frontHtml = null; self._renderOverlay(self._orderedSelected()); }); }
      return null;
    }
    if (V.place) {
      var gi = this.galleryImg, pl = V.place(src, this.handle, this.ptype, this.title, l.id, true);
      if (pl === undefined) return null;
      if (!pl) return g === null || (this.vis && this.vis.positions.indexOf(l.id) >= 0) ? { v: 'front', hide: true } : null;
      var q = pl.quad.map(function (p) { return toBox(gi, p[0], p[1]); });
      return { v: 'front', warp: true, quad: q, aw: pl.aw, ah: pl.ah, vis: true, scene: !!pl.scene };
    }
    var mm = (l && SIZES[l.id]) ? sizeOf(l) : 0;
    var s = V.spot(src, this.handle, this.ptype, this.title, l.id, mm); if (!s) return null;
    return { v: 'front', x: s.cx * 100, y: s.cy * 100, w: s.w * 100, h: s.h * 100, rot: s.rot || 0, vis: true };
  };
  // Photo shares -> shares of the picture box (the photo is drawn object-fit: contain).
  function toBox(gi, x, y) {
    if (gi && gi.naturalWidth && gi.naturalHeight && gi.clientWidth && gi.clientHeight) {
      var ir = gi.naturalWidth / gi.naturalHeight, br = gi.clientWidth / gi.clientHeight, bx = 1, by = 1, ox = 0, oy = 0;
      if (ir < br) { bx = ir / br; ox = (1 - bx) / 2; } else if (ir > br) { by = br / ir; oy = (1 - by) / 2; }
      return [ox + x * bx, oy + y * by];
    }
    return [x, y];
  }
  // Logo shape (height / width) for flat placement; loads once, then redraws.
  EnrichedPDP.prototype._logoAr = function (l) {
    if (!l) return 0.7;
    if (l.source === 'text' && l.text) return 0.32;
    var u = l.preview; if (!u) return 0.7;
    var c = this._arc || (this._arc = {}), self = this;
    if (c[u]) return c[u];
    if (c[u] !== 0) { c[u] = 0; var im = new Image(); im.onload = function () { c[u] = (im.naturalHeight / im.naturalWidth) || 0.7; self._frontHtml = null; self._renderOverlay(self._orderedSelected()); self._syncFlat(l.id); }; im.src = u; }
    return 0.7;
  };
  // Flat placement of a line inside its print area, in mm: { aw, ah, lw, lh, fx, fy }. Clamps the stored position.
  EnrichedPDP.prototype._flatPos = function (l, aw, ah) {
    var s = this.selected[l.id] || l, ar = this._logoAr(l), lw = Math.min(SIZES[l.id] ? sizeOf(l) : aw * 0.6, aw, ah / ar), lh = lw * ar;
    var fx = (s.fx == null || isNaN(s.fx)) ? (aw - lw) / 2 : +s.fx, fy = (s.fy == null || isNaN(s.fy)) ? Math.max(0, (ah - lh) / 2) : +s.fy;
    fx = Math.max(0, Math.min(aw - lw, fx)); fy = Math.max(0, Math.min(Math.max(0, ah - lh), fy));
    return { aw: aw, ah: ah, lw: lw, lh: lh, fx: fx, fy: fy };
  };
  // Sleeves sit on the photo only where the photo has a measured sleeve area; otherwise they live in the sleeve view.
  EnrichedPDP.prototype._onPhoto = function (l) { if (!l || (l.id !== 'ls' && l.id !== 'rs')) return true; var vs = this._visSpot(l); return !!(vs && vs.scene); };
  EnrichedPDP.prototype._areaFor = function (id) {
    var V = window.BMNVis; return V && V.areaDims ? V.areaDims(this.handle, this.ptype, this.title, id) : { w: 100, h: 100 };
  };
  // Printful style flat editor: the print area front-on, true to size; drag the logo where it goes.
  EnrichedPDP.prototype._flatControl = function (l) {
    if (!SPOTS[l.id] || SPOTS[l.id].v !== 'front' || !window.BMNVis || !window.BMNVis.place) return '';
    var ad = this._areaFor(l.id), fp = this._flatPos(l, ad.w, ad.h);
    return '<div class="bmn-epdp__flatctl"><div class="bmn-epdp__flat" data-pos="' + l.id + '" style="aspect-ratio:' + ad.w + '/' + ad.h + ';max-width:' + Math.round(Math.min(260, 190 * ad.w / Math.max(ad.w, ad.h) * (ad.h > ad.w ? 1 : 1.25))) + 'px">' +
      '<span class="bmn-epdp__flat-logo" data-pos="' + l.id + '" style="' + this._flatStyle(fp) + '">' + this._flatInner(l) + '</span></div>' +
      '<div class="bmn-epdp__flat-cap"><span>Print area ' + ad.w + ' x ' + ad.h + 'mm. Drag the logo to place it.</span>' +
      '<span class="bmn-epdp__flat-xy" data-pos="' + l.id + '">' + this._flatXY(fp) + '</span>' +
      '<span class="bmn-epdp__flat-btns"><button type="button" class="bmn-epdp__flat-btn" data-pos="' + l.id + '" data-flat="centre">Centre</button><button type="button" class="bmn-epdp__flat-btn" data-pos="' + l.id + '" data-flat="top">Top</button></span></div></div>';
  };
  EnrichedPDP.prototype._flatStyle = function (fp) {
    return 'left:' + (fp.fx / fp.aw * 100).toFixed(2) + '%;top:' + (fp.fy / fp.ah * 100).toFixed(2) + '%;width:' + (fp.lw / fp.aw * 100).toFixed(2) + '%;height:' + (Math.min(fp.lh, fp.ah) / fp.ah * 100).toFixed(2) + '%';
  };
  EnrichedPDP.prototype._flatXY = function (fp) { return Math.round(fp.fx) + 'mm from left, ' + Math.round(fp.fy) + 'mm from top'; };
  EnrichedPDP.prototype._flatInner = function (l) {
    if (l.preview) return '<img src="' + esc(l.preview) + '" alt="" draggable="false">';
    if (l.source === 'text' && l.text) { var f = FONTS[l.font] || FONTS.block; return '<b style="font:' + f.css + ';color:' + inkHex(l.tcolour || this._defaultInk()) + '">' + esc(f.upper ? l.text.toUpperCase() : l.text) + '</b>'; }
    return '<i>Your logo</i>';
  };
  EnrichedPDP.prototype._syncFlat = function (id) {
    var el = this.root.querySelector('.bmn-epdp__flat-logo[data-pos="' + id + '"]'), s = this.selected[id]; if (!el || !s) return;
    var l = this._orderedSelected().filter(function (x) { return x.id === id; })[0]; if (!l) return;
    var ad = this._areaFor(id), fp = this._flatPos(l, ad.w, ad.h);
    el.setAttribute('style', this._flatStyle(fp));
    var xy = this.root.querySelector('.bmn-epdp__flat-xy[data-pos="' + id + '"]'); if (xy) xy.textContent = this._flatXY(fp);
  };
  EnrichedPDP.prototype._bindFlat = function () {
    var self = this, drag = null;
    this.linesEl.addEventListener('pointerdown', function (e) {
      var lg = e.target.closest('.bmn-epdp__flat-logo'); if (!lg) return;
      var id = lg.getAttribute('data-pos'), s = self.selected[id], box = lg.parentNode; if (!s || !box) return;
      var l = self._orderedSelected().filter(function (x) { return x.id === id; })[0], ad = self._areaFor(id), fp = self._flatPos(l, ad.w, ad.h);
      drag = { id: id, x: e.clientX, y: e.clientY, fx: fp.fx, fy: fp.fy, k: ad.w / box.clientWidth };
      try { lg.setPointerCapture(e.pointerId); } catch (er) {}
      e.preventDefault(); self._viewFor(id);
    });
    this.linesEl.addEventListener('pointermove', function (e) {
      if (!drag) return; var s = self.selected[drag.id]; if (!s) return;
      s.fx = drag.fx + (e.clientX - drag.x) * drag.k; s.fy = drag.fy + (e.clientY - drag.y) * drag.k;
      var l = self._orderedSelected().filter(function (x) { return x.id === drag.id; })[0], ad = self._areaFor(drag.id), fp = self._flatPos(l, ad.w, ad.h);
      s.fx = fp.fx; s.fy = fp.fy; self._syncFlat(drag.id); self._frontHtml = null; self._renderOverlay(self._orderedSelected());
    });
    function end() { if (drag) { drag = null; if (self._recompute) self._recompute(); } }
    this.linesEl.addEventListener('pointerup', end); this.linesEl.addEventListener('pointercancel', end);
    this.linesEl.addEventListener('click', function (e) {
      var b = e.target.closest('.bmn-epdp__flat-btn'); if (!b) return;
      var id = b.getAttribute('data-pos'), s = self.selected[id]; if (!s) return;
      var l = self._orderedSelected().filter(function (x) { return x.id === id; })[0], ad = self._areaFor(id), fp = self._flatPos(l, ad.w, ad.h);
      s.fx = (ad.w - fp.lw) / 2; if (b.getAttribute('data-flat') === 'top') s.fy = 0; else s.fy = Math.max(0, (ad.h - fp.lh) / 2);
      self._syncFlat(id); self._viewFor(id); self._frontHtml = null; self._renderOverlay(self._orderedSelected());
    });
  };
  EnrichedPDP.prototype._markHtml = function (l, spot) {
    if (spot && spot.hide) return '';
    if (spot && spot.warp) return this._warpHtml(l, spot);
    var w = spot.w;
    if (isTeam(l) && !spot.vis) {
      var tb = this._teamBox(l), mpp = spot.mmPct || MM_PER_PCT[spot.v] || 6, mn = this.root.querySelector('.bmn-epdp__gallery-main'), rt = mn && mn.clientHeight ? mn.clientWidth / mn.clientHeight : 1;
      var tst = 'left:' + spot.x + '%;top:' + spot.y + '%;width:' + (tb.w / mpp).toFixed(2) + '%;height:' + (tb.h / mpp * rt).toFixed(2) + '%' + (l.rot ? ';transform:translate(-50%,-50%) rotate(' + l.rot + 'deg)' : '');
      return '<span class="bmn-epdp__mk bmn-epdp__mk--team" data-pos="' + l.id + '" data-mmpct="' + mpp.toFixed(3) + '" style="' + tst + '">' + this._teamSvg(l, tb) + '</span>';
    }
    if (!spot.vis && l && SIZES[l.id]) w = sizeOf(l) / (spot.mmPct || MM_PER_PCT[spot.v]);
    var isText = l && l.source === 'text' && l.text;
    var url = l && l.source !== 'text' && l.preview, grow = 1, self = this;
    if (url && spot.vis && window.BMNVis) {
      var px = ((this.galleryImg && this.galleryImg.clientWidth) || 500) * spot.w / 100;
      var st = window.BMNVis.styled(l.preview, l.method === 'dtf' ? 'dtf' : 'emb', px, function () { self._frontHtml = null; self._renderOverlay(self._orderedSelected()); });
      if (st) { url = st; grow = 1.13; }
    }
    // Uploaded logo off the photo (back, sleeve view): the box takes the logo's own shape, so 30 cm wide shows 30 cm wide.
    var mn0 = this.root.querySelector('.bmn-epdp__gallery-main'), rt0 = mn0 && mn0.clientHeight ? mn0.clientWidth / mn0.clientHeight : 1;
    var hh = spot.vis ? spot.h : (url && l ? w * this._logoAr(l) * rt0 : w * (isText ? 0.45 : 0.8));
    var style = 'left:' + spot.x + '%;top:' + spot.y + '%;width:' + (w * grow).toFixed(2) + '%;height:' + (hh * grow).toFixed(2) + '%' + (((spot.rot || 0) + ((l && l.rot) || 0)) ? ';transform:translate(-50%,-50%) rotate(' + ((spot.rot || 0) + ((l && l.rot) || 0)) + 'deg)' : '');
    var dpos = l ? ' data-pos="' + l.id + '" data-mmpct="' + (spot.mmPct || MM_PER_PCT[spot.v] || 6).toFixed(3) + '"' + (spot.gtop != null ? ' data-sy="' + (spot.sy != null ? spot.sy : spot.y).toFixed(3) + '" data-gtop="' + spot.gtop.toFixed(3) + '" data-gh="' + spot.gh.toFixed(3) + '"' + (spot.hood ? ' data-hood="1"' : '') : '') : '';
    if (url) return '<span class="bmn-epdp__mk"' + dpos + ' style="' + style + '"><img src="' + esc(url) + '" alt=""' + (grow > 1 ? ' style="filter:none"' : '') + '></span>';
    if (l && l.source === 'text' && l.text) {
      var f = FONTS[l.font] || FONTS.block, t = f.upper ? l.text.toUpperCase() : l.text;
      return '<span class="bmn-epdp__mk bmn-epdp__mk--text"' + dpos + ' style="' + style + '"><svg viewBox="0 0 100 40" preserveAspectRatio="xMidYMid meet"><text x="50" y="27" text-anchor="middle" font-size="26" style="font:' + f.css + ';font-size:26px" fill="' + inkHex(l.tcolour) + '"' + (l.tcolour === 'white' ? ' stroke="rgba(0,0,0,.25)" stroke-width=".4"' : '') + '>' + esc(t) + '</text></svg></span>';
    }
    var label = !l ? 'Your logo' : (l.source === 'text' ? 'Your text' : (l.source === 'redraw' ? 'Your logo' : 'Your logo'));
    return '<span class="bmn-epdp__mk bmn-epdp__mk--mark" style="' + style + '"><span>' + label + '</span></span>';
  };
  // A flat print area (aw x ah mm) drawn front-on, then mapped onto the photo by its 4 corners.
  EnrichedPDP.prototype._warpHtml = function (l, spot) {
    var gi = this.galleryImg, W = (gi && gi.clientWidth) || 500, H = (gi && gi.clientHeight) || 500, self = this;
    var EW = 400, EH = Math.round(EW * spot.ah / spot.aw);
    var q = spot.quad.map(function (p) { return [p[0] * W, p[1] * H]; });
    var m = window.BMNVis.matrix3d(EW, EH, q);
    var fp = this._flatPos(l || { id: 'lc' }, spot.aw, spot.ah);
    var inner, url = l && l.source !== 'text' && l.preview, grow = 1;
    if (url) {
      var qw = Math.hypot(q[1][0] - q[0][0], q[1][1] - q[0][1]), px = qw * fp.lw / spot.aw;
      var st = window.BMNVis.styled(l.preview, l.method === 'dtf' ? 'dtf' : 'emb', px, function () { self._frontHtml = null; self._renderOverlay(self._orderedSelected()); });
      if (st) { url = st; grow = 1.13; }
    }
    var gw = fp.lw * grow, gh = Math.min(fp.lh, spot.ah) * grow, gx = fp.fx - (gw - fp.lw) / 2, gy = fp.fy - (gh - Math.min(fp.lh, spot.ah)) / 2;
    var box = 'left:' + (gx / spot.aw * 100).toFixed(2) + '%;top:' + (gy / spot.ah * 100).toFixed(2) + '%;width:' + (gw / spot.aw * 100).toFixed(2) + '%;height:' + (gh / spot.ah * 100).toFixed(2) + '%' + (l && l.rot ? ';transform:rotate(' + l.rot + 'deg)' : '');
    if (url) inner = '<span class="bmn-epdp__wk" style="' + box + '"><img src="' + esc(url) + '" alt=""' + (grow > 1 ? ' style="filter:none"' : '') + '></span>';
    else if (l && l.source === 'text' && l.text) {
      var f = FONTS[l.font] || FONTS.block, t = f.upper ? l.text.toUpperCase() : l.text;
      inner = '<span class="bmn-epdp__wk bmn-epdp__mk--text" style="' + box + '"><svg viewBox="0 0 100 40" preserveAspectRatio="xMidYMid meet"><text x="50" y="27" text-anchor="middle" font-size="26" style="font:' + f.css + ';font-size:26px" fill="' + inkHex(l.tcolour) + '"' + (l.tcolour === 'white' ? ' stroke="rgba(0,0,0,.25)" stroke-width=".4"' : '') + '>' + esc(t) + '</text></svg></span>';
    } else inner = '<span class="bmn-epdp__wk bmn-epdp__mk--mark" style="' + box + '"><span>Your logo</span></span>';
    return '<span class="bmn-epdp__mk bmn-epdp__mk--warp' + ((url || (l && l.source === 'text' && l.text)) ? ' has-logo' : '') + '"' + (l ? ' data-pos="' + l.id + '"' : '') + ' style="width:' + EW + 'px;height:' + EH + 'px;transform:' + m + '"><span class="bmn-epdp__warea"></span>' + inner + '</span>';
  };
  // Fit each text preview to its box once it is in the page.
  function fitTexts(box) {
    Array.prototype.forEach.call(box.querySelectorAll('.bmn-epdp__mk--text svg'), function (svg) {
      try { var bb = svg.querySelector('text').getBBox(); if (bb.width) svg.setAttribute('viewBox', (bb.x - 2) + ' ' + (bb.y - 2) + ' ' + (bb.width + 4) + ' ' + (bb.height + 4)); } catch (e) {}
    });
  }

  EnrichedPDP.prototype._renderOverlay = function (lines) {
    if (!this.galleryImg) return;
    var main = this.root.querySelector('.bmn-epdp__gallery-main');
    if (!main) return;
    var self = this;
    // One-off build of the front marks layer, the back view and the Front / Back switch
    if (!this.frontMarks) {
      this.frontMarks = document.createElement('span'); this.frontMarks.className = 'bmn-epdp__marks'; main.appendChild(this.frontMarks);
      var bv = document.createElement('span'); bv.className = 'bmn-epdp__backview'; bv.hidden = true;
      if (this.backPhoto && !(this.vis && HEADWEAR.test(this.vis.tpl))) {
        bv.className += ' bmn-epdp__backview--photo';
        bv.innerHTML = '<img class="bmn-epdp__back-photo" alt="Back of the garment" crossorigin="anonymous" style="visibility:hidden" src="' + esc(this.backPhoto) + '">' + '<span class="bmn-epdp__marks"></span><span class="bmn-epdp__back-tag">Back view</span>';
      } else bv.innerHTML = backSvg(this.vis && this.vis.tpl) + '<span class="bmn-epdp__marks"></span><span class="bmn-epdp__back-tag">Back view</span>';
      main.appendChild(bv); this.backMarks = bv.querySelector('.bmn-epdp__marks');
      // Sleeve view: drawn sleeves, used when the photo has no measured sleeve area (arms-down hoodies, jackets).
      var sv = document.createElement('span'); sv.className = 'bmn-epdp__sideview'; sv.hidden = true;
      sv.innerHTML = '<svg class="bmn-epdp__side-svg" viewBox="0 0 100 100" aria-hidden="true"><path class="bmn-epdp__side-body" d="M16 16Q28 9 40 16L38 84Q28 89 18 84Z"/><path class="bmn-epdp__side-body" d="M60 16Q72 9 84 16L82 84Q72 89 62 84Z"/><path d="M16 16Q28 9 40 16L38 84Q28 89 18 84ZM60 16Q72 9 84 16L82 84Q72 89 62 84Z" fill="none" stroke="rgba(0,0,0,.18)" stroke-width=".5"/><text x="28" y="96" text-anchor="middle" font-size="4" fill="rgba(15,15,15,.55)">LEFT SLEEVE</text><text x="72" y="96" text-anchor="middle" font-size="4" fill="rgba(15,15,15,.55)">RIGHT SLEEVE</text></svg><span class="bmn-epdp__marks"></span><span class="bmn-epdp__back-tag">Sleeves</span>';
      main.appendChild(sv); this.sideView = sv; this.sideMarks = sv.querySelector('.bmn-epdp__marks');
      main.addEventListener('click', function (e) { if (self.view === 'back' || e.target.closest('.bmn-epdp__mk')) e.preventDefault(); });
      var sw = document.createElement('div'); sw.className = 'bmn-epdp__views'; sw.setAttribute('role', 'group'); sw.setAttribute('aria-label', 'Garment side');
      sw.innerHTML = '<button type="button" class="bmn-epdp__view-btn is-on" data-view="front" aria-pressed="true">Front</button><button type="button" class="bmn-epdp__view-btn" data-view="back" aria-pressed="false">Back</button><button type="button" class="bmn-epdp__view-btn" data-view="side" aria-pressed="false" hidden>Sleeves</button>';
      if (this.vis && HEADWEAR.test(this.vis.tpl)) { sw.hidden = true; sw.style.display = 'none'; }
      main.parentNode.insertBefore(sw, main.nextSibling);
      sw.addEventListener('click', function (e) { var b = e.target.closest('.bmn-epdp__view-btn'); if (b) self._setView(b.getAttribute('data-view')); });
      if (this.overlay) this.overlay.hidden = true;
    }
    var view = this.view || 'front';
    var sw2 = this._swatchFor(this.colour);
    var colourImg = sw2 ? sw2.getAttribute('data-image') : null;
    var onColourPhoto = !colourImg || this.galleryImg.getAttribute('src') === colourImg;
    var front = [], back = [], side = [], hasContent = false, ratio = (main.clientWidth || 1) / (main.clientHeight || 1);
    lines.forEach(function (l) {
      var sp = SPOTS[l.id]; if (!sp) return;
      if (l.preview || (l.source === 'text' && l.text)) hasContent = true;
      if (sp.v === 'front') {
        var vs = self._visSpot(l);
        // Sleeves only go on the photo when the photo has a measured sleeve area; otherwise the sleeve view.
        if ((l.id === 'ls' || l.id === 'rs') && !(vs && vs.scene)) sp = { v: 'side', x: l.id === 'ls' ? 28 : 72, y: 34, w: 15 };
        else sp = vs || sp;
      }
      else if (l.id === 'np' && self.vis && self.vis.tpl === 'hoodie') sp = { v: 'back', x: 50, y: 19, w: 7 };
      if (sp.v === 'back') sp = self._backPhotoSpot(sp, l.id) || sp;
      // Moved by the customer (assets/bmn-pdp-direct.js): offset in mm from the standard spot.
      if (!sp.warp && !sp.hide && typeof sp.x === 'number' && (l.ox || l.oy)) {
        var mp = sp.mmPct || MM_PER_PCT[sp.v] || 6, c = {}; for (var k in sp) c[k] = sp[k];
        c.sy = sp.y; c.x = sp.x + (l.ox || 0) / mp; c.y = sp.y + (l.oy || 0) / mp * ratio; sp = c;
      }
      (sp.v === 'back' ? back : sp.v === 'side' ? side : front).push(self._markHtml(l, sp));
    });
    if (!lines.length) { var fid = (this.vis && this.vis.positions.length && this.vis.positions.indexOf('lc') < 0) ? this.vis.positions[0] : 'lc'; front.push(this._markHtml(null, this._visSpot({ id: fid }) || SPOTS[fid] || SPOTS.lc)); }
    var fh = onColourPhoto ? front.join('') : '';
    if (fh !== this._frontHtml) { this.frontMarks.innerHTML = fh; this._frontHtml = fh; fitTexts(this.frontMarks); }
    var bh = back.join('');
    if (bh !== this._backHtml) { this.backMarks.innerHTML = bh; this._backHtml = bh; }
    if (this.sideMarks) {
      var sh = side.join('');
      if (sh !== this._sideHtml) { this.sideMarks.innerHTML = sh; this._sideHtml = sh; fitTexts(this.sideMarks); }
      var sbtn = this.root.querySelector('.bmn-epdp__view-btn[data-view="side"]'); if (sbtn) sbtn.hidden = !side.length;
      if (!side.length && this.view === 'side') { this.view = 'front'; this._setView('front'); return; }
      Array.prototype.forEach.call(this.sideView.querySelectorAll('.bmn-epdp__side-body'), function (pth) { pth.setAttribute('fill', self._garmentColour()); });
    }
    var body = this.root.querySelector('.bmn-epdp__back-body'), collar = this.root.querySelector('.bmn-epdp__back-collar');
    var col = this._garmentColour();
    if (body) body.setAttribute('fill', col);
    this._tintBack(col);
    if (collar) collar.setAttribute('fill', col);
    if (view === 'back') fitTexts(this.backMarks);
    var tag = this.root.querySelector('.bmn-epdp__back-tag');
    if (tag) tag.textContent = 'Back view, ' + displayName(this.colour);
    if (this.overlayNote) this.overlayNote.hidden = !hasContent;
  };

  // ---------- real back photo (designer plus, 27 Oct 2026) ----------
  // The back photo is one colour (supplier flat back shot). Measure the garment on it, map the drawn back view's
  // positions onto it, and recolour it in Lab to the colour picked so shading and seams stay.
  EnrichedPDP.prototype._backPhotoSpot = function (sp, id) {
    var V = window.BMNVis, self = this;
    var ba = id && this.areas && this.areas.back && this.areas.back[id];
    if (ba && !ba.off) {
      // Hand-set back area: shares of the back photo, or of the drawn back view when there is no photo.
      if (!this.backPhoto || this.areas.backSvg) return { v: 'back', x: ba.cx * 100, y: ba.cy * 100, w: sp.w, mmPct: (ba.mm || 280) / (ba.w * 100) };
      var im = this._bpImg, gm = V && V.measured(this.backPhoto), ar = (gm && gm.ar) || (im && im.naturalWidth ? im.naturalHeight / im.naturalWidth : 1);
      var bx0 = 1, by0 = 1, ox0 = 0, oy0 = 0;
      if (ar > 1) { bx0 = 1 / ar; ox0 = (1 - bx0) / 2; } else if (ar < 1) { by0 = ar; oy0 = (1 - by0) / 2; }
      return { v: 'back', x: (ox0 + ba.cx * bx0) * 100, y: (oy0 + ba.cy * by0) * 100, w: sp.w, mmPct: (ba.mm || 280) / (ba.w * bx0 * 100) };
    }
    var tpl = (this.vis && this.vis.tpl) || '';
    if (!tpl) { var pt = String(this.ptype || '') + ' ' + String(this.title || ''); tpl = /hood|zoodie/i.test(pt) ? 'hoodie' : /softshell|fleece|gilet|body ?warmer/i.test(pt) ? 'fleece' : ''; }
    if (!this.backPhoto || !V || HEADWEAR.test(tpl)) return null;
    var g = V.measured(this.backPhoto);
    if (g === undefined) {
      if (!this._bpMeasuring) { this._bpMeasuring = true; V.measure(this.backPhoto).then(function () { self._backHtml = null; self._renderOverlay(self._orderedSelected()); }); }
      return null;
    }
    if (!g) return null;
    var isHood = /^hoodie$/.test(tpl) || /hood|zoodie/i.test(String(this.ptype || '') + ' ' + String(this.title || ''));
    var top = isHood ? 0.5 : /^(softshell|fleece|gilet)$/.test(tpl) ? 2.5 : 7.5;
    var fy = (sp.y - top) / (95 - top);
    // On a real hoodie photo the hood hangs over the upper back, so keep the mark just below it.
    // Other garments: the photo starts at the collar, so keep the mark clear of the neckline.
    var minF = isHood ? 0.32 : 0.14;
    if (fy < minF) fy = minF;
    var x = g.cx + (sp.x - 50) / 56 * g.tw, y = g.top + fy * g.h;
    var bx = 1, by = 1, ox = 0, oy = 0;
    if (g.ar > 1) { bx = 1 / g.ar; ox = (1 - bx) / 2; } else if (g.ar < 1) { by = g.ar; oy = (1 - by) / 2; }
    var t = V.reg && V.reg.templates && V.reg.templates[tpl], chest = (t && t.chest_mm) || 520;
    // gtop and gh (garment top and height, % of the box) let the print area sit a set way below the collar.
    return { v: 'back', x: (ox + x * bx) * 100, y: (oy + y * by) * 100, w: sp.w, mmPct: chest / (g.tw * bx * 100), gtop: (oy + g.top * by) * 100, gh: g.h * by * 100, hood: isHood };
  };
  function srgbToLin(c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
  function linToSrgb(c) { c = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055; return Math.max(0, Math.min(255, Math.round(c * 255))); }
  function labF(t) { return t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116; }
  function labFi(t) { return t > 0.206893 ? t * t * t : (t - 16 / 116) / 7.787; }
  function rgbToLab(r, g, b) {
    var R = srgbToLin(r), G = srgbToLin(g), B = srgbToLin(b);
    var X = (R * 0.4124 + G * 0.3576 + B * 0.1805) / 0.95047, Y = R * 0.2126 + G * 0.7152 + B * 0.0722, Z = (R * 0.0193 + G * 0.1192 + B * 0.9505) / 1.08883;
    var fx = labF(X), fy = labF(Y), fz = labF(Z);
    return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
  }
  function labToRgb(L, a, b) {
    var fy = (L + 16) / 116, fx = fy + a / 500, fz = fy - b / 200;
    var X = labFi(fx) * 0.95047, Y = labFi(fy), Z = labFi(fz) * 1.08883;
    var R = X * 3.2406 - Y * 1.5372 - Z * 0.4986, G = -X * 0.9689 + Y * 1.8758 + Z * 0.0415, B = X * 0.0557 - Y * 0.2040 + Z * 1.0570;
    return [linToSrgb(R), linToSrgb(G), linToSrgb(B)];
  }
  function parseCol(v) {
    var s = String(v || '').trim(), m = s.match(/rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
    if (m) return [+m[1], +m[2], +m[3]];
    var h = s.replace('#', ''); if (h.length === 3) h = h.replace(/(.)/g, '$1$1');
    if (/^[0-9a-f]{6}$/i.test(h)) return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    return [155, 155, 155];
  }
  function tintCanvas(img, hex) {
    var W = Math.min(900, img.naturalWidth || 900), H = Math.round(W * img.naturalHeight / img.naturalWidth);
    var c = document.createElement('canvas'); c.width = W; c.height = H;
    var x = c.getContext('2d', { willReadFrequently: true }); x.drawImage(img, 0, 0, W, H);
    var d = x.getImageData(0, 0, W, H), p = d.data, n = W * H, i;
    var T = rgbToLab.apply(null, parseCol(hex));
    var wt = new Float32Array(n), Ls = new Float32Array(n), sum = 0, cnt = 0;
    for (i = 0; i < n; i++) {
      var o = i * 4, r = p[o], g = p[o + 1], b = p[o + 2];
      var dist = 765 - r - g - b + (Math.max(r, g, b) - Math.min(r, g, b)) * 2;
      var w = Math.max(0, Math.min(1, (dist - 18) / 40)); wt[i] = w;
      if (w > 0) { var L = rgbToLab(r, g, b)[0]; Ls[i] = L; if (w > 0.9) { sum += L; cnt++; } }
    }
    var Lm = cnt ? sum / cnt : 50;
    for (i = 0; i < n; i++) {
      var w2 = wt[i]; if (!w2) continue;
      var o2 = i * 4, L2 = Math.max(0, Math.min(100, T[0] + (Ls[i] - Lm) * 0.85));
      var rgb = labToRgb(L2, T[1], T[2]);
      p[o2] = rgb[0] * w2 + p[o2] * (1 - w2); p[o2 + 1] = rgb[1] * w2 + p[o2 + 1] * (1 - w2); p[o2 + 2] = rgb[2] * w2 + p[o2 + 2] * (1 - w2);
    }
    x.putImageData(d, 0, 0);
    return c;
  }
  EnrichedPDP.prototype._tintBack = function (hex) {
    var el = this.root.querySelector('.bmn-epdp__back-photo'), self = this;
    if (!el || !this.backPhoto) return;
    this._tints = this._tints || {};
    if (this._tintFor === hex) return;
    this._tintFor = hex;
    if (this._tints[hex]) { el.src = this._tints[hex]; el.style.visibility = ''; return; }
    var go = function (im) {
      try { var url = tintCanvas(im, hex).toDataURL('image/jpeg', 0.9); self._tints[hex] = url; if (self._tintFor === hex) { el.src = url; el.style.visibility = ''; } }
      catch (e) { el.src = self.backPhoto; el.style.visibility = ''; }
    };
    if (this._bpImg) return go(this._bpImg);
    var im = new Image(); im.crossOrigin = 'anonymous';
    im.onload = function () { self._bpImg = im; if (self._tintFor) { var hx = self._tintFor; self._tintFor = null; self._tintBack(hx); } };
    im.onerror = function () { el.style.visibility = ''; };
    im.src = this.backPhoto;
  };

  // ---------- small API for assets/bmn-designer-plus.js ----------
  EnrichedPDP.prototype.designState = function () {
    var self = this;
    var sw = this._swatchFor(this.colour);
    return {
      handle: this.handle, ptype: this.ptype, title: this.title, colour: displayName(this.colour), colourValue: this.colour,
      photo: (sw && sw.getAttribute('data-image')) || (this.galleryImg && (this.galleryImg.currentSrc || this.galleryImg.src)) || '',
      lines: this._orderedSelected().map(function (l) { var fr = (SPOTS[l.id] || {}).v === 'front' && self._onPhoto(l) && window.BMNVis && window.BMNVis.areaDims, ad = fr ? self._areaFor(l.id) : null, fp = fr ? self._flatPos(l, ad.w, ad.h) : null; return { id: l.id, label: l.label, method: l.method, source: l.source, text: l.text, preview: l.preview, mm: fp ? fp.lw : (SIZES[l.id] ? sizeOf(l) : 0), fx: fp ? fp.fx : null, fy: fp ? fp.fy : null, side: (SPOTS[l.id] || {}).v || 'front' }; }),
      swatches: this.swatches.map(function (s) { return { value: s.getAttribute('data-value'), name: displayName(s.getAttribute('data-value')), image: s.getAttribute('data-image') || '' }; })
    };
  };
  EnrichedPDP.prototype.pickColour = function (value) {
    var sw = this._swatchFor(value); if (sw) sw.click();
  };
  EnrichedPDP.prototype.showView = function (v) { this._setView(v); };
  // Setup tool: try new print areas live without saving.
  EnrichedPDP.prototype.applyAreas = function (a) {
    this.areas = a; AREA_MM = areaMM(a);
    if (window.BMNVis && window.BMNVis.setAreas) window.BMNVis.setAreas(this.handle, a);
    if (this._resetVis) this._resetVis();
    this._frontHtml = null; this._backHtml = null; this._renderOverlay(this._orderedSelected());
  };

  // ---------- money ----------
  function fmt(pence, withCurrency) {
    var v = pence / 100;
    var format = (window.BMNVat && (withCurrency ? window.BMNVat.moneyCurrencyFormat : window.BMNVat.moneyFormat)) || '£{{amount}}';
    var amount = v.toFixed(2);
    return format.replace(/\{\{\s*(amount|amount_with_comma_separator|amount_no_decimals|amount_no_decimals_with_comma_separator)\s*\}\}/g, function (m, t) {
      if (t.indexOf('no_decimals') !== -1) return String(Math.round(v));
      return amount;
    });
  }
  EnrichedPDP.prototype._dual = function (pence) {
    var wc = window.BMNVat ? window.BMNVat.currencyCodeEnabled : false;
    var inc = Math.round(pence * 1.2);
    return '<span class="vat-inc">' + fmt(inc, wc) + '</span><span class="vat-ex">' + fmt(pence, wc) + '</span>';
  };
  EnrichedPDP.prototype._plain = function (pence) { return fmt(pence, false); };

  // ---------- spec ----------
  EnrichedPDP.prototype._specText = function () {
    var self = this;
    var lines = this._orderedSelected();
    var sizes;
    if (this.v3) {
      var orders = this._colourOrders();
      sizes = orders.length > 1
        ? orders.map(function (o) { return displayName(o.colour) + ': ' + o.sizes; }).join('; ')
        : (orders[0] ? orders[0].sizes : '');
    } else {
      sizes = this.sizeRows.map(function (row) {
        var q = parseInt(row.querySelector('.bmn-epdp__qty').value, 10) || 0;
        return q > 0 ? (row.getAttribute('data-size') + ' x' + q) : null;
      }).filter(Boolean).join(', ');
    }
    var dec = lines.map(function (l) {
      var src = l.source === 'upload' ? ('uploaded' + (l.fileName ? ': ' + l.fileName : '')) : (l.source === 'text' ? ((isTeam(l) ? (function () { var tb = self._teamBox(l), nd = teamNeeds(l); return (nd.name && nd.num ? 'names and numbers' : nd.num ? 'numbers' : 'names') + ' on each garment (' + (nd.name ? 'name ' + tb.nameH / 10 + 'cm high' : '') + (nd.name && nd.num ? ', ' : '') + (nd.num ? 'number ' + tb.numH / 10 + 'cm high' : '') + '): ' + self._rosterText(l, '; '); })() : l.perName ? ('a different name on each garment: ' + (self.v3 ? self._rosterText(l, '; ') : splitNames(l.names).join(', '))) : ('text: "' + (l.text || '') + '"')) + (self.v3 ? ', ' + fontLabel(l.font) + ' font, ' + inkLabel(l.tcolour) : '')) : (l.source === 'redraw' ? 'redraw requested' : 'supply later'));
      var method = l.method === 'dtf' ? 'DTF print' : ('Embroidery' + (l.highStitch ? ' (high stitch)' : ''));
      if (l.oversized) method += ', oversized';
      var front0 = self.v3 && SPOTS[l.id] && SPOTS[l.id].v === 'front' && self._onPhoto(l) && window.BMNVis && window.BMNVis.areaDims, ad0 = front0 ? self._areaFor(l.id) : null, fp0 = front0 ? self._flatPos(l, ad0.w, ad0.h) : null;
      if (self.v3 && SIZES[l.id]) method += ', ' + (fp0 ? Math.round(fp0.lw) : isTeam(l) ? Math.round(self._teamBox(l).w) : sizeOf(l)) + 'mm wide';
      if (l.rot) method += ', rotated ' + Math.abs(l.rot) + ' degrees ' + (l.rot > 0 ? 'clockwise' : 'anticlockwise');
      if (l.ox || l.oy) method += ', moved ' + Math.abs(Math.round(l.ox || 0)) + 'mm ' + ((l.ox || 0) < 0 ? 'left' : 'right') + ' and ' + Math.abs(Math.round(l.oy || 0)) + 'mm ' + ((l.oy || 0) < 0 ? 'up' : 'down') + ' from standard';
      if (fp0) {
        method += ', placed ' + Math.round(fp0.fx) + 'mm from the left and ' + Math.round(fp0.fy) + 'mm from the top of the ' + ad0.w + ' x ' + ad0.h + 'mm print area';
      }
      return l.label + ', ' + method + ' (' + src + ')';
    }).join('; ');
    return { sizes: sizes, dec: dec || 'none yet', lines: lines };
  };

  EnrichedPDP.prototype._properties = function () {
    var t = this._specText();
    var total = this._totalUnits();
    var apps = total * t.lines.length;
    var perShirtDecoration = 0, self = this;
    t.lines.forEach(function (l) { perShirtDecoration += self._rateForLine(l, apps); });
    var perShirt = this.blank + perShirtDecoration + (this._expressOn(t.lines) ? this.expressFee : 0);
    var subtotal = perShirt * total;
    var hasEmb = t.lines.some(function (l) { return l.method === 'embroidery' && l.source !== 'text'; });
    var setupInfo = this._setupInfo(t.lines, subtotal);
    var setup = (hasEmb && !this.repeat) ? this.setupFee * setupInfo.count : 0;
    this._setupCount = setup ? setupInfo.count : 0;
    var colourText = displayName(this.colour);
    if (this.v3) {
      var ords = this._colourOrders();
      if (ords.length > 1) colourText = ords.map(function (o) { return displayName(o.colour) + ' (' + o.units + ')'; }).join(', ');
    }
    return {
      'Colour': colourText,
      'Sizes': t.sizes,
      'Applications': apps + ' (' + total + ' x ' + t.lines.length + ')',
      'Decoration': t.dec,
      'Per shirt (ex VAT)': this._plain(perShirt),
      'Order total (ex VAT)': this._plain(subtotal + setup),
      'Turnaround': this._expressOn(t.lines) ? ('Express, ready to ship ' + this.expressDays + ' working days from mockup approval (' + this._plain(this.expressFee) + ' per item)') : 'Standard',
      'Setup': setup ? (this._plain(setup) + ' new-logo setup') : (hasEmb ? (this.repeat ? 'none (repeat order)' : (setupInfo.designs === 0 && setupInfo.inBasket ? 'design already in basket' : 'free, first design (' + this._setupRuleText() + ')')) : 'none (no setup charge)'),
      'Artwork': t.lines.some(function (l) { return l.source === 'redraw'; }) ? 'Redraw requested, from £25 ex VAT, price confirmed on the free mockup before production.' : (t.lines.some(function (l) { return l.source === 'later'; }) ? 'To follow after the order. Free mockup for approval before production.' : (t.lines.length ? 'Supplied with the order' : 'No logo')),
      '_spec': JSON.stringify({ product: { handle: this.handle, title: this.title }, colour: this.colour, colours: this.v3 ? this._colourOrders().map(function (o) { return { colour: o.colour, units: o.units, sizes: o.sizes }; }) : undefined, positions: t.lines.map(function (l) { var c = {}; for (var k in l) if (k !== 'preview') c[k] = l[k]; return c; }), totalUnits: total, applications: apps, repeat: this.repeat })
    };
  };

  // ---------- cart ----------
  EnrichedPDP.prototype._buildItems = function () {
    var props = this._properties();
    var items = [];
    if (this.v3) {
      // Each colour's sizes are their own basket lines; the decoration spec covers the whole order.
      this._colourOrders().forEach(function (o) {
        o.items.forEach(function (it) { items.push({ id: it.id, quantity: it.quantity, properties: props }); });
      });
      return items;
    }
    this.qtyInputs.forEach(function (i) {
      var q = parseInt(i.value, 10) || 0;
      if (q > 0 && i.getAttribute('data-variant-id')) items.push({ id: parseInt(i.getAttribute('data-variant-id'), 10), quantity: q, properties: props });
    });
    return items;
  };

  // Returns the Branding basket lines for the chosen positions, or null if a rate has no matching variant.
  EnrichedPDP.prototype._brandingItems = function (lines, total) {
    var self = this, out = [], apps = total * lines.length, missing = false;
    lines.forEach(function (l) {
      var rate = self._rateForLine(l, apps);
      if (!rate) return;
      var vid = self.brandingVariants[String(rate)];
      if (!vid) { missing = true; return; }
      var method = l.method === 'dtf' ? 'Print' : 'Embroidery';
      if (l.oversized) method += ', large';
      var vs = self._visSpot(l);
      var spec = { style: self.root.getAttribute('data-style') || '', handle: self.handle, colour: displayName(self.colour), position: l.id, label: l.label,
        method: l.method, width_mm: SIZES[l.id] ? sizeOf(l) : null, offset_mm: (l.ox || l.oy) ? { x: Math.round(l.ox || 0), y: Math.round(l.oy || 0) } : null, rotate_deg: l.rot || 0, high_stitch: !!l.highStitch, oversized: !!l.oversized, artwork: l.source,
        names: (l.source === 'text' && l.perName) ? (self.v3 ? self._rosterRows(l).map(function (w) { return { colour: displayName(w.colour), size: w.size, name: w.name, number: w.num }; }) : splitNames(l.names)) : null,
        team: isTeam(l) ? { show: l.teamMode, name_mm: l.nameH, number_mm: l.numH, font: l.font, colour: l.tcolour } : null,
        template: self.vis ? self.vis.tpl : null, placement: (vs && typeof vs.x === 'number') ? { x: +vs.x.toFixed(1), y: +vs.y.toFixed(1), w: +vs.w.toFixed(1) } : ((vs && vs.quad) ? { quad: vs.quad.map(function (p) { return [+(+p[0]).toFixed(4), +(+p[1]).toFixed(4)]; }), aw: vs.aw, ah: vs.ah } : null) };
      var bp = { 'Position': l.label, 'Method': method, 'For': self.title, '_Production spec': JSON.stringify(spec) };
      if (l.source === 'text' && l.perName) bp['Names'] = self.v3 ? self._rosterText(l) : splitNames(l.names).join(', ');
      else if (l.source === 'text' && l.text) bp['Text'] = l.text;
      if (window.__bmnRights && l.source !== 'text' && l.source !== 'later') bp['Artwork rights'] = window.__bmnRights;
      out.push({ id: parseInt(vid, 10), quantity: total, properties: bp });
    });
    return missing ? null : out;
  };

  // Visualiser v1: render the order proof (colour photo with every uploaded logo) for the basket.
  // bmn-site-logo.js attaches it to the first Branding line as '_Proof image'. Never blocks the sale: 3s cap.
  EnrichedPDP.prototype._proofReady = function (lines) {
    var V = window.BMNVis, self = this; window.__bmnProof = null;
    if (!V || !this.galleryImg) return Promise.resolve();
    // Proof v2 (1 Nov 2026): every position the customer placed, logos and text, on the real photos.
    // Front photo (chest and measured sleeves), then the back view and the sleeve view, side by side.
    var front = this._proofFront(lines).then(function (c) { return c || self._proofPanel('front', lines); });
    var work = Promise.all([front, this._proofPanel('back', lines), this._proofPanel('side', lines)]).then(function (cs) {
      cs = cs.filter(Boolean); if (!cs.length) return null;
      var H = 1000, gap = 24, W = 0, sc = cs.map(function (c) { var w = Math.round(c.width * H / c.height); W += w; return w; });
      W += gap * (cs.length - 1);
      var out = document.createElement('canvas'); out.width = W; out.height = H;
      var x = out.getContext('2d'); x.fillStyle = '#fff'; x.fillRect(0, 0, W, H);
      var at = 0; cs.forEach(function (c, i) { x.drawImage(c, at, 0, sc[i], H); at += sc[i] + gap; });
      return new Promise(function (res) { out.toBlob(function (b) { res(b); }, 'image/jpeg', 0.85); });
    }).then(function (b) {
      if (b) window.__bmnProof = { blob: b, at: Date.now(), name: (self.handle || 'garment') + '-' + String(displayName(self.colour)).toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-proof.jpg' };
    });
    return Promise.race([work, new Promise(function (r) { setTimeout(r, 4000); })]).catch(function () {});
  };
  // Name and number block as an image, the shown roster row.
  EnrichedPDP.prototype._teamImage = function (l) {
    var b = this._teamBox(l), svg = this._teamSvg(l, b).replace('<svg ', '<svg xmlns="http://www.w3.org/2000/svg" width="' + Math.round(b.w * 4) + '" height="' + Math.round(b.h * 4) + '" ');
    var f = FONTS[l.font] || FONTS.block, k = 4, ink = inkHex(l.tcolour || this._defaultInk());
    var draw = function () {
      var c = document.createElement('canvas'); c.width = Math.round(b.w * k); c.height = Math.round(b.h * k); var x = c.getContext('2d');
      x.textAlign = 'center'; x.textBaseline = 'alphabetic'; x.fillStyle = ink;
      if (b.need.name && b.name) { x.font = f.css.replace('1em', (b.nameH / CAP * b.nameScale * k).toFixed(1) + 'px'); x.fillText(b.name, c.width / 2, b.nameH * k); }
      if (b.need.num && b.num) { x.font = f.css.replace('1em', (b.numH / CAP * k).toFixed(1) + 'px'); x.fillText(b.num, c.width / 2, b.h * k); }
      return c.toDataURL('image/png');
    };
    var ld = document.fonts && document.fonts.load ? document.fonts.load(f.css.replace('1em', '100px')).catch(function () {}) : Promise.resolve();
    return Promise.race([ld, new Promise(function (r) { setTimeout(r, 800); })]).then(draw);
  };
  // Text as an image (1000 x 320, the shape the builder uses for text), in the chosen font and ink.
  EnrichedPDP.prototype._textImage = function (l) {
    var f = FONTS[l.font] || FONTS.block, t = f.upper ? l.text.toUpperCase() : l.text, ink = inkHex(l.tcolour || this._defaultInk());
    var font = function (px) { return f.css.replace('1em', px + 'px'); };
    var draw = function () {
      var c = document.createElement('canvas'); c.width = 1000; c.height = 320; var x = c.getContext('2d');
      var px = 260; x.font = font(px); var w = x.measureText(t).width; if (w > 980) { px = Math.floor(px * 980 / w); x.font = font(px); }
      x.textAlign = 'center'; x.textBaseline = 'middle'; x.fillStyle = ink;
      if (l.tcolour === 'white') { x.lineWidth = 2; x.strokeStyle = 'rgba(0,0,0,.25)'; x.strokeText(t, 500, 165); }
      x.fillText(t, 500, 165); return c.toDataURL('image/png');
    };
    var ld = document.fonts && document.fonts.load ? document.fonts.load(font(260), t).catch(function () {}) : Promise.resolve();
    return Promise.race([ld, new Promise(function (r) { setTimeout(r, 800); })]).then(draw);
  };
  // Front: the colour photo with every front logo and text, sleeves included where the photo has a measured sleeve.
  EnrichedPDP.prototype._proofFront = function (lines) {
    var V = window.BMNVis, self = this;
    if (!this.vis || !V.composite) return Promise.resolve(null);
    var sw = this._swatchFor(this.colour), src = (sw && sw.getAttribute('data-image')) || this.galleryImg.currentSrc || this.galleryImg.src;
    if (!src) return Promise.resolve(null);
    if (src.indexOf('//') === 0) src = location.protocol + src;
    var front = lines.filter(function (l) {
      if (!/^(lc|rc|cc|ff|ls|rs)$/.test(l.id)) return false;
      if (!(l.preview && l.source !== 'text') && !(l.source === 'text' && l.text)) return false;
      return self._onPhoto(l);
    });
    if (!front.length) return Promise.resolve(null);
    var big = src.replace(/([?&])width=\d+/, '$1width=1200');
    return Promise.all([V.loadImg(big)].concat(front.map(function (l) {
      return (l.source === 'text' ? self._textImage(l) : Promise.resolve(l.preview)).then(function (u) { return V.loadImg(u); });
    }))).then(function (r) {
      var items = front.map(function (l, i) { var ad = self._areaFor(l.id), fp = self._flatPos(l, ad.w, ad.h); return { pos: l.id, logo: r[i + 1], method: l.method === 'dtf' ? 'dtf' : 'emb', mm: fp.lw, fx: fp.fx, fy: fp.fy, rot: l.rot || 0 }; });
      return V.composite(r[0], self.handle, self.ptype, self.title, items, 1200);
    }).catch(function () { return null; });
  };
  // Back or sleeve view: redraw what the customer sees (real back photo in their colour, or the drawn view) with each mark.
  EnrichedPDP.prototype._proofPanel = function (kind, lines) {
    var V = window.BMNVis, self = this;
    var el = kind === 'back' ? this.root.querySelector('.bmn-epdp__backview') : kind === 'front' ? this.root.querySelector('.bmn-epdp__gallery-main') : this.sideView;
    var marks = kind === 'back' ? this.backMarks : kind === 'front' ? this.frontMarks : this.sideMarks;
    if (!el || !marks) return Promise.resolve(null);
    var mks = Array.prototype.slice.call(marks.querySelectorAll('.bmn-epdp__mk[data-pos]:not(.bmn-epdp__mk--warp)')).map(function (mk) {
      var l = lines.filter(function (x) { return x.id === mk.getAttribute('data-pos'); })[0];
      if (!l || !((l.preview && l.source !== 'text') || (l.source === 'text' && l.text))) return null;
      var st = mk.style, img = mk.querySelector('img');
      return { l: l, x: parseFloat(st.left) / 100, y: parseFloat(st.top) / 100, w: parseFloat(st.width) / 100, h: parseFloat(st.height) / 100, src: img ? img.src : null, rot: parseFloat((/rotate\(([-\d.]+)deg\)/.exec(st.transform || '') || [])[1]) || 0 };
    }).filter(function (m) { return m && !isNaN(m.x) && !isNaN(m.w); });
    if (!mks.length) return Promise.resolve(null);
    var main = this.root.querySelector('.bmn-epdp__gallery-main');
    var bw = (main && main.clientWidth) || 600, bh = (main && main.clientHeight) || 600;
    var PW = 900, PH = Math.round(PW * bh / bw);
    var photo = kind === 'front' ? this.galleryImg : el.querySelector('img.bmn-epdp__back-photo'), svg = kind === 'front' ? null : el.querySelector('svg');
    var base;
    if (photo && (photo.currentSrc || photo.src)) base = V.loadImg(photo.currentSrc || photo.src).then(function (im) { return { im: im, photo: true }; });
    else if (svg) {
      var xml = new XMLSerializer().serializeToString(svg);
      if (xml.indexOf('xmlns=') < 0) xml = xml.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
      base = V.loadImg('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml)).then(function (im) { return { im: im, photo: false }; });
    } else return Promise.resolve(null);
    return Promise.all([base].concat(mks.map(function (m) {
      return (isTeam(m.l) ? self._teamImage(m.l) : m.l.source === 'text' ? self._textImage(m.l) : Promise.resolve(m.src || m.l.preview)).then(function (u) { return V.loadImg(u); }).catch(function () { return null; });
    }))).then(function (r) {
      var c = document.createElement('canvas'); c.width = PW; c.height = PH; var x = c.getContext('2d');
      x.fillStyle = '#fff'; x.fillRect(0, 0, PW, PH);
      var b = r[0], inset = b.photo ? 0 : 0.06;
      if (b.photo) {
        var ir = b.im.naturalWidth / b.im.naturalHeight, dw = PW, dh = PW / ir; if (dh > PH) { dh = PH; dw = PH * ir; }
        x.drawImage(b.im, (PW - dw) / 2, (PH - dh) / 2, dw, dh);
      } else { var sq = Math.min(PW, PH) * 0.88; x.drawImage(b.im, (PW - sq) / 2, (PH - sq) / 2, sq, sq); }
      var mx = PW * inset, my = PH * inset, mw = PW * (1 - 2 * inset), mh = PH * (1 - 2 * inset);
      mks.forEach(function (m, i) {
        var im = r[i + 1]; if (!im) return;
        var cw = m.w * mw, ch = m.h * mh, ar = im.naturalHeight / im.naturalWidth, dw2 = cw, dh2 = cw * ar;
        if (dh2 > ch) { dh2 = ch; dw2 = ch / ar; }
        x.save(); x.translate(mx + m.x * mw, my + m.y * mh); if (m.rot) x.rotate(m.rot * Math.PI / 180);
        x.drawImage(im, -dw2 / 2, -dh2 / 2, dw2, dh2); x.restore();
      });
      return c;
    }).catch(function () { return null; });
  };

  EnrichedPDP.prototype._addToCart = function () {
    var self = this;
    var lines = this._orderedSelected();
    var total = this._totalUnits();
    if (total === 0) { this._say('Add a size quantity first.', 'error'); return; }
    // any position needing artwork now? if all are "later", route to quote instead
    var needsArt = lines.some(function (l) { return l.source === 'upload' && !l.fileName; });
    if (needsArt) { this._say('Choose a file for each "upload" position, or switch it to "supply later".', 'error'); return; }
    if (this.v3) {
      var badR = lines.filter(function (l) { return l.source === 'text' && l.perName && self._rosterFilled(l) < total; })[0];
      if (badR) { var nd0 = teamNeeds(badR), left = total - this._rosterFilled(badR); this._say(left + ' of ' + total + ' garment' + (total !== 1 ? 's' : '') + ' still need ' + (nd0.name && nd0.num ? 'a name and number' : nd0.num ? 'a number' : 'a name') + ' on ' + badR.label + '. Fill every row, or untick the names box.', 'error'); return; }
    }
    var badNames = this.v3 ? null : lines.filter(function (l) { return l.source === 'text' && l.perName && splitNames(l.names).length !== total; })[0];
    if (badNames) { var nn = splitNames(badNames.names).length; this._say('You have ' + nn + ' name' + (nn !== 1 ? 's' : '') + ' for ' + total + ' garment' + (total !== 1 ? 's' : '') + ' on ' + badNames.label + '. Add one name per garment, or untick "Different name on each garment".', 'error'); return; }
    // "Supply logo later" goes straight to the basket. The artwork follows after the order and the
    // free mockup is sent for approval before production. The mockup form is only for the mockup button.

    var items = this._buildItems();
    // Logo charge: one Branding line per position, quantity = garments, at the rate card price.
    var brand = this._brandingItems(lines, total);
    if (brand === null) { this._say('We could not price this logo online. Please use the WhatsApp quote or email info@brandmenow.co.uk and we will send it over.', 'error'); return; }
    items = items.concat(brand);
    // Batch id: ties this order's logo lines to its own garments, so a second batch of the same
    // garment with different branding (or names) never merges with or resizes this one in the basket.
    var batch = Date.now().toString(36);
    items.forEach(function (it) { if (it.properties && (it.properties.Decoration || it.properties.Position)) it.properties._Batch = batch; });
    if (this._expressOn(lines)) items.push({ id: parseInt(this.expressVariantId, 10), quantity: total, properties: { 'Position': 'Express turnaround', 'Method': this.expressDays + ' working days', 'For': this.title } });
    // setup fee line, if a variant is configured and applicable
    if (this.setupVariantId) {
      var props = this._properties();
      if (/new-logo setup$/.test(props['Setup'])) items.push({ id: parseInt(this.setupVariantId, 10), quantity: this._setupCount || 1, properties: { 'For order': this.title } });
    }
    this.addBtns.forEach(function (b) { b.classList.add('is-loading'); b.disabled = true; });
    self._proofReady(lines).then(function () { return fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items }) }); })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
      .then(function (res) {
        if (!res.ok) { self._say((res.d && (res.d.description || res.d.message)) || 'Could not add to cart.', 'error'); return; }
        self._say('Added ' + total + ' item' + (total !== 1 ? 's' : '') + ' with your decoration spec. Redirecting to cart...', 'success');
        self._refreshCart();
        setTimeout(function () { window.location.href = '/cart'; }, 900);
      })
      .catch(function () { self._say('Something went wrong. Please try again or use WhatsApp quote.', 'error'); })
      .finally(function () { self.addBtns.forEach(function (b) { b.classList.remove('is-loading'); b.disabled = self._totalUnits() === 0; }); });
  };

  EnrichedPDP.prototype._refreshCart = function () {
    fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (c) {
      document.querySelectorAll('.cart-count-bubble').forEach(function (b) { b.hidden = c.item_count === 0; var v = b.querySelector('[aria-hidden="true"]'); if (v) v.textContent = c.item_count; });
    }).catch(function () {});
  };

  // ---------- quote / lead ----------
  EnrichedPDP.prototype._quoteMessage = function () {
    var t = this._specText();
    var total = this._totalUnits();
    return 'Quote request from the product page.\n' +
      'Product: ' + this.title + ' (' + this.handle + ')\n' +
      'Colour: ' + (this._properties()['Colour'] || '-') + '\n' +
      'Sizes: ' + (t.sizes || '-') + '  (total ' + total + ')\n' +
      'Decoration: ' + t.dec + '\n' +
      'Please send an itemised branded quote and a free mockup.';
  };

  EnrichedPDP.prototype._whatsapp = function () {
    var num = this.whatsapp.replace(/[^0-9]/g, '');
    if (!num) return;
    window.open('https://wa.me/' + num + '?text=' + encodeURIComponent(this._quoteMessage()), '_blank');
  };

  EnrichedPDP.prototype._openLead = function (reason) {
    if (!this.lead) { this._whatsapp(); return; }
    var h = this.lead.querySelector('.bmn-epdp__lead-head');
    if (h) h.textContent = reason === 'later' ? 'Supply your logo later, free mockup' : 'See it with your logo, free mockup';
    this.lead.hidden = false;
    var f = this.lead.querySelector('[name="email"]'); if (f) setTimeout(function () { f.focus(); }, 50);
  };

  EnrichedPDP.prototype._submitLead = function () {
    var self = this;
    var name = (this.lead.querySelector('[name="name"]') || {}).value || '';
    var email = (this.lead.querySelector('[name="email"]') || {}).value || '';
    if (!name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) { this._leadSay('Please add your name and a valid email.'); return; }
    if (!this.leadUrl) { this._leadSay('Please use WhatsApp quote, or email info@brandmenow.co.uk.'); return; }
    var btn = this.lead.querySelector('.bmn-epdp__lead-submit'); if (btn) { btn.disabled = true; btn.textContent = 'Sending...'; }
    var parts = name.trim().split(/\s+/);
    var body = { fields: [
      { name: 'email', value: email.trim() },
      { name: 'firstname', value: parts.shift() || name },
      { name: 'lastname', value: parts.join(' ') },
      { name: 'message', value: this._quoteMessage() }
    ], context: { pageUri: location.href, pageName: document.title } };
    var isHs = /hsforms\.com/i.test(this.leadUrl);
    fetch(this.leadUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(isHs ? body : { name: name, email: email, message: this._quoteMessage() }) })
      .then(function (r) { return r.ok; })
      .then(function (ok) {
        if (ok) { self.lead.innerHTML = '<div class="bmn-epdp__lead-done">Thanks ' + esc(parts.length ? name.split(/\s+/)[0] : name) + '. Your request is on its way, we will send your mockup and itemised quote, usually within one working day.</div>'; }
        else self._leadSay('That did not send. Please use WhatsApp quote or email info@brandmenow.co.uk.');
      })
      .catch(function () { self._leadSay('That did not send. Please use WhatsApp quote or email info@brandmenow.co.uk.'); })
      .finally(function () { if (btn) { btn.disabled = false; btn.textContent = 'Send my request'; } });
  };
  EnrichedPDP.prototype._leadSay = function (t) { var m = this.lead.querySelector('.bmn-epdp__lead-msg'); if (m) m.textContent = t; };

  EnrichedPDP.prototype._say = function (t, type) { if (!this.msg) return; this.msg.textContent = t; this.msg.className = 'bmn-epdp__msg bmn-epdp__msg--' + type; this.msg.hidden = false; };
  EnrichedPDP.prototype._clearMsg = function () { if (this.msg) { this.msg.hidden = true; this.msg.textContent = ''; } };

  function init() {
    document.querySelectorAll('.bmn-epdp').forEach(function (el) { if (!el.dataset.epdpInit) { el.dataset.epdpInit = '1'; try { el.__epdp = new EnrichedPDP(el); } catch (e) { console.warn('[BMN enriched PDP]', e); } } });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
